import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Button } from '../../../../../components/ui/button';
import type { MaterialData } from '../../../types/materials';
import type { DashboardProject } from '../../../types/project';
import { PipeSize } from '../../../types/setup';
import { DocumentTag } from '@/features/project/types';
import { pipeSizes } from '../../../mocks/material';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import {
  NumberInput,
  parseNonNegativeInteger,
  parseNonNegativeNumber,
} from '../../../../../components/ui/NumberInput';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { AddMaterial, AddMaterialCategory, getListMaterialCategory, getPreSignedUrlToUpload } from '../../../../../redux/Project/materialSaga';
import { Notification } from '../../../../../components/ui/custom-notification';
import { convertSetupValue, formatSetupValue, convertValueToInches, convertValueFromInches } from '../../../../../utils/unitUtils';
import { useCertifications } from '../../../hooks/useCertifications';
import { AntdDate } from '../../../../../components/ui/antd-date';
import dayjs from 'dayjs';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../constants/fileUpload';
import {
  createEmptyWarehouseSegment,
  resizeWarehouseSegments,
  type WarehousePipeSegment,
} from '../../../../analysis/utils/warehouseMaterialPayload';
import { logPipeInventoryMaterialAdded } from '../../../../analysis/utils/pipeInventoryAudit';
import { runPipeInventoryAddPrefill } from '../../../../analysis/utils/runPipeInventoryAddPrefill';
import {
  isBendsFlangesLayoutFabricSubType,
  isBuckleArrestorFabricSubType,
  isDualDiameterMaterialCategory,
  isPipeLikeMaterialCategory,
  PIPE_LIKE_MATERIAL_CATEGORIES,
} from './materialCategoryConstants';
interface AddMaterialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (material: MaterialData) => void;
  selectedPipeSize: number;
  onPipeSizeChange?: (pipeSizeIndex: number) => void;
  project: DashboardProject;
  pipeSizesData: any[];
  unit: string;
  setupData: any
  documentTags: DocumentTag[];
  setDocumentTags: (any: any) => void;
  /**
   * Pipe Inventory → Add Project Material only.
   * Hides Material Category (always Pipe), adds Date Recorded / MTR / pups→segments.
   * Must stay false for project Materials tab so existing flows are unchanged.
   */
  pipeInventoryMode?: boolean;
  /** Optional seed from Pipe Fitter inventory + action */
  seedPipeNo?: string;
  seedPipePipeId?: string;
}

// Validation rules for different material categories
const validationRules = {
  Pipe: ['pipeNo'],
  Forge: ['forgeNRI',],
  Gas: ['gasSerialNumber', 'gasLotNumber'],
  'Wire Lot': ['wireLotNumber'],
  Coating: ['coatingLotNumber',],
  NDE: ['consumableType'],
  Misc: ['miscQuantity'],
  Bends: ['pipeNo'],
  Flanges: ['pipeNo'],
  Reducer: ['pipeNo'],
  'T-Piece': ['pipeNo'],
  Weldolet: ['pipeNo'],
  'End Cap': ['pipeNo'],
  Sweepolet: ['pipeNo'],
  Hydro: ['pipeNo'],
  Torquing: ['pipeNo'],
  Gauging: ['pipeNo'],
  Flushing: ['pipeNo'],
};

// Unit options: display full names in dropdown, save short forms
const UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: "in", label: "Inch" },
  { value: "cm", label: "Centimeter" },
  { value: "mm", label: "Millimeter" },
  { value: "ft", label: "Foot" },
  { value: "m", label: "Meter" },
];

// Field labels for error messages
const fieldLabels = {
  // status: 'Status',
  pipeNRI: 'Pipe NRI',
  pipeNo: 'Pipe No',
  pipeHeatNumber: 'Heat Number',
  pipeLength: 'Length',
  pipeOuterDiameter: 'Outer Diameter',
  pipeOuterDiameter2: 'Diameter 2',
  pipeWallThickness: 'Wall Thickness',
  pipeWallThickness2: 'Wall Thickness 2',
  forgeNRI: 'Forge NRI',
  forgeOrderNumber: 'Forge Order Number',
  forgeSerialNumber: 'Forge Serial Number',
  forgeHeatNumber: 'Forge Heat Number',
  gasSerialNumber: 'Gas Serial Number',
  gasType: 'Gas Type',
  gasLotNumber: 'Gas Lot Number',
  wireLotNumber: 'Wire Lot Number',
  wireType: 'Wire Type',
  coatingLotNumber: 'Coating Lot Number',
  coatingManufacturer: 'Coating Manufacturer',
  coatingBatchNumber: 'Coating Batch Number',

  wireManufacturer: 'Wire Manufacturer',
  gasManufacturer: 'Gas Manufacturer',
  forgeManufacturer: 'Forge Manufacturer',
  ndeManufacturer: 'NDE Manufacturer',

  wireBatchNumber: 'Wire Batch Number',
  gasBatchNumber: 'Gas Batch Number',
  forgeBatchNumber: 'Forge Batch Number',
  consumableType: 'Consumable Type',
  miscQuantity: 'Quantity',
};

export const AddMaterialDialog: React.FC<AddMaterialDialogProps> = ({
  isOpen,
  onClose,
  onAdd,
  selectedPipeSize,
  onPipeSizeChange,
  project,
  pipeSizesData,
  unit,
  setupData,
  documentTags,
  setDocumentTags,
  pipeInventoryMode = false,
  seedPipeNo,
  seedPipePipeId,
}) => {
  // Use certifications hook


  // const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [pipeSize, setPipeSize] = useState<PipeSize[]>(pipeSizes as unknown as PipeSize[]);
  const [newMaterial, setNewMaterial] = useState<Partial<MaterialData>>({
    materialCategory: 'Pipe',
    pipeId: 0,
    materialStatus: 'Available',
    details: {}
  });

  const [isAddingTag, setIsAddingTag] = useState(false);
  const [isAddingPipeGrade, setIsAddingPipeGrade] = useState(false);
  const [isAddingMaterialType, setIsAddingMaterialType] = useState(false);
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  //const [selectedPipeGrade, setSelectedPipeGrade] = useState<string[]>([]);
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingPipes, setIsUpdatingPipes] = useState(false);
  const [currentPipeSizeIndex, setCurrentPipeSizeIndex] = useState(selectedPipeSize);
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  // These arrays store the string representation of tag IDs (e.g., "101", "102")
  const [selectedMaterialType, setSelectedMaterialType] = useState<string[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string[]>([]);
  const [selectedPipeGrade, setSelectedPipeGrade] = useState<string[]>([]);
  const [selectedProjectStage, setSelectedProjectStage] = useState<string[]>([]);
  const [selectedCoatingManufacturer, setSelectedCoatingManufacturer] = useState<string[]>([]);
  const [selectedGasManufacturer, setSelectedGasManufacturer] = useState<string[]>([]);
  const [selectedWireManufacturer, setSelectedWireManufacturer] = useState<string[]>([]);
  const [selectedNDEManufacturer, setSelectedNDEManufacturer] = useState<string[]>([]);
  const [ndeExpDate, setNdeExpDate] = useState<string>('');
  const [ndeMaterialId, setNdeMaterialId] = useState('');
  const [ndeSelectedLocation, setNdeSelectedLocation] = useState('');
  const [ndeNotes, setNdeNotes] = useState('');
  const [selectedPipe, setSelectedPipe] = useState<any>(pipeSizesData[selectedPipeSize]);
  const [materialOptions, setMaterialOptions] = useState<any[]>(['Pipe', 'Forge', 'Gas', 'Wire Lot', 'Coating'])
  // Local state for documentTags to ensure immediate updates
  const [localDocumentTags, setLocalDocumentTags] = useState<DocumentTag[]>(documentTags);
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [isAddingWorkOrderNumber, setIsAddingWorkOrderNumber] = useState(false);
  const [selectedWorkOrderNumber, setSelectedWorkOrderNumber] = useState<string[]>([]);
  const [isAddingPONumber, setIsAddingPONumber] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingPipeType, setIsAddingPipeType] = useState(false);
  const [isAddingPipePslLevel, setIsAddingPipePslLevel] = useState(false);
  const [isAddingPipeDelivery, setIsAddingPipeDelivery] = useState(false);
  const [isAddingMiscLot, setIsAddingMiscLot] = useState(false);
  const [isAddingMiscMaterialType, setIsAddingMiscMaterialType] = useState(false);
  // Material unit state for Pipe: OD, Wall, Length, Length remaining
  const [materialUnit, setMaterialUnit] = useState({
    pipeOuterDiameter: unit as string,
    pipeWallThickness: unit as string,
    pipeLength: unit as string,
    pipeLengthRemaining: unit as string,
  });
  /** Pipe Inventory Add only — Fitter end confirm + setup OD/wall prefill */
  const [fitterPrefillLoading, setFitterPrefillLoading] = useState(false);
  const [fitterPrefillMessage, setFitterPrefillMessage] = useState<string | null>(null);
  const [fitterPrefillOk, setFitterPrefillOk] = useState(false);
  const materialUnitRef = useRef(materialUnit);
  materialUnitRef.current = materialUnit;
  const newMaterialRef = useRef(newMaterial);
  newMaterialRef.current = newMaterial;
  const handleMaterialUnitChange = (field: keyof typeof materialUnit, newUnit: string) => {
    const valueFieldMap: Record<keyof typeof materialUnit, keyof any> = {
      pipeOuterDiameter: 'pipeOuterDiameter',
      pipeWallThickness: 'pipeWallThickness',
      pipeLength: 'pipeLength',
      pipeLengthRemaining: 'pipeLengthRemaining',
    };
    const valueField = valueFieldMap[field];
    const oldUnit = materialUnit[field];
    const currentValue = Number(newMaterial.details?.[valueField]) || 0;
    const valueInInches = convertValueToInches(currentValue, oldUnit);
    const convertedValue = convertValueFromInches(valueInInches, newUnit);
    setMaterialUnit(prev => ({ ...prev, [field]: newUnit }));
    const updates: Record<string, number> = { [valueField]: convertedValue };
    if (field === 'pipeOuterDiameter' && newMaterial.details?.pipeOuterDiameter2 != null && newMaterial.details.pipeOuterDiameter2 !== '') {
      const diam2InInches = convertValueToInches(Number(newMaterial.details.pipeOuterDiameter2), oldUnit);
      updates.pipeOuterDiameter2 = convertValueFromInches(diam2InInches, newUnit);
    }
    if (field === 'pipeWallThickness' && newMaterial.details?.pipeWallThickness2 != null && newMaterial.details.pipeWallThickness2 !== '') {
      const wt2InInches = convertValueToInches(Number(newMaterial.details.pipeWallThickness2), oldUnit);
      updates.pipeWallThickness2 = convertValueFromInches(wt2InInches, newUnit);
    }
    setNewMaterial(prev => ({
      ...prev,
      details: { ...prev.details, ...updates }
    }));
  };
  // Filter material options based on project type
  useEffect(() => {
    if (selectedPipe && selectedPipe.projectType) {
      let filteredOptions: string[] = [];

      if (selectedPipe.projectType === 'pipeline') {
        // For pipeline projects, show Pipe, Misc, and Coating (if enabled)
        filteredOptions = ['Pipe', 'Misc'];
        if (newMaterial.materialCategory === 'Forge' || newMaterial.materialCategory === 'Gas' || newMaterial.materialCategory === 'Wire Lot' || newMaterial.materialCategory === 'NDE') {
          setNewMaterial({
            ...newMaterial,
            materialCategory: 'Pipe'
          })
        }

        if (selectedPipe.coating) {
          filteredOptions.push('Coating');
        }
      } else if (String(selectedPipe.projectType).toLowerCase() === 'fabrication') {
        if (isBendsFlangesLayoutFabricSubType(selectedPipe.projectSubType)) {
          filteredOptions = [...PIPE_LIKE_MATERIAL_CATEGORIES, 'Gas', 'Wire Lot', 'Misc'];
        } else if (isBuckleArrestorFabricSubType(selectedPipe.projectSubType)) {
          // Buckle Arrestor: existing categories + Flanges (with Material Type / length fields)
          filteredOptions = ['Pipe', 'Forge', 'Flanges', 'Gas', 'Wire Lot', 'Misc'];
        } else {
          filteredOptions = ['Pipe', 'Forge', 'Gas', 'Wire Lot', 'Misc'];
        }
        if (selectedPipe.coating) {
          filteredOptions.push('Coating');
        }
      } else {
        // For other project types, show all options
        filteredOptions = ['Pipe', 'Forge', 'Gas', 'Wire Lot', 'Coating', 'Misc'];
      }

      setMaterialOptions(filteredOptions);
    }
  }, [selectedPipe, currentPipeSizeIndex]);


  const user = JSON.parse(localStorage.getItem('user') || '{}');


  // useEffect(() => {
  //   const loadInitialData = async () => {
  //     // Load departments and companies in parallel
  //     const [tags] = await Promise.all([
  //       getListMaterialCategory(),

  //     ]);

  //     // Remove duplicates and sort companies alphabetically by company_name
  //     const uniqueTags = tags
  //       .filter((tag: any, index: number, self: any[]) =>
  //         index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
  //       )
  //       .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

  //     setDocumentTags(uniqueTags);
  //   }
  //   if(!isOpen && documentTags.length === 0){
  //     loadInitialData();
  //   }
  // }, []);

  // Effect to handle initial data restoration when dialog opens
  useEffect(() => {
    if (isOpen) {


      // Sync internal state with prop
      setCurrentPipeSizeIndex(selectedPipeSize);

      // Initialize form with proper data
      if (pipeSizesData && pipeSizesData[selectedPipeSize]) {
        const selectedPipe = pipeSizesData[selectedPipeSize];
        setSelectedPipe(selectedPipe);

        setNewMaterial({
          materialCategory: 'Pipe',
          pipeId: Number(selectedPipe.pipeId),
          materialStatus: 'Available',
          projectTypeId: Number(selectedPipe.projectTypeId),
          projectType: selectedPipe.projectType,
          projectSubType: selectedPipe.projectSubType || 'none',

          details: {
            // Initialize with pipe data values
            // pipeNRI: selectedPipe.pipeNRI || '',
            // pipeNo: selectedPipe.pipeNo || '',
            // pipeHeatNumber: selectedPipe.pipeHeatNumber || '',
            // pipeLength: convertSetupValue(selectedPipe.pipeLength) || '',
            // pipeOuterDiameter: convertSetupValue(selectedPipe.pipeOuterDiameter) || '',
            // pipeWallThickness: convertSetupValue(selectedPipe.pipeWallThickness) || '',
            // // Initialize other details as empty
            // forgeNRI: '',
            // forgeOrderNumber: '',
            // forgeSerialNumber: '',
            // forgeHeatNumber: '',
            // gasSerialNumber: '',
            // lotNumber: ''
          }
        });
      } else {
        // Fallback to default values
        setNewMaterial({
          materialCategory: 'Pipe',
          pipeId: 0,
          materialStatus: 'Available',
          details: {}
        });
      }

      // Reset other form state
      setSelectedTags([]);
      setMaterialDocuments([]);
      setIsAddingTag(false);
      setNewTag('');
      setIsSubmitting(false);
    }
  }, [isOpen, selectedPipeSize, pipeSizesData]);



  // Sync local state with parent state when documentTags change
  useEffect(() => {
    setLocalDocumentTags(documentTags);
  }, [documentTags]);

  // Initialize local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalDocumentTags(documentTags);
    }
  }, [isOpen, documentTags]);

  // Validation helper function
  const validateMaterialFields = (category: string, material: any): string | null => {

    const requiredFields = [
      ...(validationRules[category as keyof typeof validationRules] || []),
      ...(pipeInventoryMode && category === 'Pipe' ? (['pipeHeatNumber'] as const) : []),
    ];

    for (const field of requiredFields) {
      // Check if field is at top level (like status) or in details
      const value = material[field] || material.details?.[field];
      const numVal = typeof value === 'number' ? value : Number(value);

      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'number' && value <= 0) ||
        (field === 'miscQuantity' && (!Number.isFinite(numVal) || numVal <= 0))
      ) {
        const errorMessage = `${fieldLabels[field as keyof typeof fieldLabels]} is required`;

        return errorMessage;
      }
    }


    return null;
  };

  // Seed Pipe Id / size line once when dialog opens from Fitter + (avoid setState loops).
  const inventorySeedAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !pipeInventoryMode) {
      inventorySeedAppliedRef.current = null;
      return;
    }
    const setupPipeId = pipeSizesData?.[selectedPipeSize]?.pipeId;
    const fromSetup =
      setupPipeId != null && String(setupPipeId).trim() !== '' ? String(setupPipeId).trim() : '';
    const fromSeed = String(seedPipePipeId ?? '').trim();
    const seededPipePipeId = fromSeed || fromSetup;
    const seededPipeNo = String(seedPipeNo ?? '').trim();
    const seedKey = `${seededPipeNo}|${seededPipePipeId}|${selectedPipeSize}`;
    if (inventorySeedAppliedRef.current === seedKey) return;
    inventorySeedAppliedRef.current = seedKey;

    setNewMaterial((prev) => {
      const nextPipeNo = seededPipeNo || String(prev.details?.pipeNo ?? '').trim();
      const nextPipePipeId =
        seededPipePipeId || String(prev.details?.pipePipeId ?? '').trim();
      const dateRecorded =
        (prev.details as { dateRecorded?: string } | undefined)?.dateRecorded ||
        dayjs().format('YYYY-MM-DD');
      const prevSeg = (prev.details as { pipeSegmentation?: unknown } | undefined)
        ?.pipeSegmentation;
      const pipeSegmentation = Array.isArray(prevSeg)
        ? (prevSeg as WarehousePipeSegment[])
        : [];
      const mtrReceived = Boolean(
        (prev.details as { mtrReceived?: boolean } | undefined)?.mtrReceived
      );

      if (
        prev.materialCategory === 'Pipe' &&
        String(prev.details?.pipeNo ?? '').trim() === nextPipeNo &&
        String(prev.details?.pipePipeId ?? '').trim() === nextPipePipeId &&
        (prev.details as { dateRecorded?: string } | undefined)?.dateRecorded === dateRecorded
      ) {
        return prev;
      }

      return {
        ...prev,
        materialCategory: 'Pipe',
        details: {
          ...prev.details,
          ...(nextPipeNo ? { pipeNo: nextPipeNo } : {}),
          ...(nextPipePipeId ? { pipePipeId: nextPipePipeId } : {}),
          dateRecorded,
          mtrReceived,
          pipeSegmentation,
        },
      };
    });
  }, [isOpen, pipeInventoryMode, selectedPipeSize, pipeSizesData, seedPipeNo, seedPipePipeId]);

  const syncPipeInventoryFitterPrefill = useCallback(async () => {
    if (!pipeInventoryMode) return;

    const pipeNo =
      String(newMaterialRef.current?.details?.pipeNo ?? '').trim() ||
      String(seedPipeNo ?? '').trim();
    if (!pipeNo) {
      setFitterPrefillMessage(null);
      setFitterPrefillOk(false);
      return;
    }

    const setupPipeId = pipeSizesData?.[currentPipeSizeIndex]?.pipeId;
    const pipePipeId =
      String(newMaterialRef.current?.details?.pipePipeId ?? '').trim() ||
      String(seedPipePipeId ?? '').trim() ||
      (setupPipeId != null ? String(setupPipeId).trim() : '');

    setFitterPrefillLoading(true);
    setFitterPrefillMessage(null);
    setFitterPrefillOk(false);

    try {
      const result = await runPipeInventoryAddPrefill({
        mode: 'add',
        projectId: project?.project?.projectId,
        projectNumber: project?.project?.projectNumber,
        projectName: project?.project?.projectName,
        pipeNo,
        pipePipeId,
        pipeSizesData: (pipeSizesData || []) as Array<Record<string, unknown>>,
      });

      if (result.status === 'skipped') {
        return;
      }

      if (result.status === 'ok') {
        const units = materialUnitRef.current;
        setNewMaterial((prev) => ({
          ...prev,
          details: {
            ...prev.details,
            pipeNo: result.pipeNo,
            pipePipeId: result.pipePipeId || prev.details?.pipePipeId,
            pipeOuterDiameter: convertValueFromInches(
              result.pipeOuterDiameterInches,
              units.pipeOuterDiameter
            ),
            pipeWallThickness: convertValueFromInches(
              result.pipeWallThicknessInches,
              units.pipeWallThickness
            ),
          },
        }));
        setFitterPrefillOk(true);
        setFitterPrefillMessage('OD / wall filled from project pipe setup (Pipe Fitter end confirmed).');
        return;
      }

      if (result.status === 'not_found') {
        setFitterPrefillMessage(result.reason);
        return;
      }

      if (result.status === 'missing_setup') {
        setFitterPrefillMessage(
          'Pipe size was not found in project pipe setup. Enter OD / wall manually or pick another size.'
        );
        return;
      }

      if (result.status === 'error') {
        setFitterPrefillMessage(result.message || 'Pipe Fitter lookup failed.');
      }
    } catch (error: unknown) {
      setFitterPrefillMessage(
        error instanceof Error ? error.message : 'Pipe Fitter lookup failed.'
      );
    } finally {
      setFitterPrefillLoading(false);
    }
  }, [
    pipeInventoryMode,
    seedPipeNo,
    seedPipePipeId,
    pipeSizesData,
    currentPipeSizeIndex,
    project,
  ]);

  // Auto-run Fitter/setup prefill once when opened from Fitter + with a pipeNo seed
  const autoSeedSyncRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen || !pipeInventoryMode) {
      autoSeedSyncRef.current = null;
      return;
    }
    const key = `${seedPipeNo || ''}|${seedPipePipeId || ''}`;
    if (!String(seedPipeNo ?? '').trim()) return;
    if (autoSeedSyncRef.current === key) return;
    autoSeedSyncRef.current = key;
    const timer = window.setTimeout(() => {
      void syncPipeInventoryFitterPrefill();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, pipeInventoryMode, seedPipeNo, seedPipePipeId, syncPipeInventoryFitterPrefill]);

  const inventorySegments: WarehousePipeSegment[] = Array.isArray(
    (newMaterial.details as { pipeSegmentation?: WarehousePipeSegment[] } | undefined)?.pipeSegmentation
  )
    ? ((newMaterial.details as { pipeSegmentation: WarehousePipeSegment[] }).pipeSegmentation)
    : [];

  const setInventorySegments = (segments: WarehousePipeSegment[], numberOfPups?: string | number | '') => {
    setNewMaterial((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        pipeSegmentation: segments,
        pipeExtraDetails: {
          ...(prev.details?.pipeExtraDetails || {}),
          numberOfPups:
            numberOfPups === undefined
              ? prev.details?.pipeExtraDetails?.numberOfPups
              : numberOfPups === '' || numberOfPups == null
                ? ''
                : parseNonNegativeInteger(String(numberOfPups)),
        },
      },
    }));
  };

  const handleInventoryPupsChange = (value: number | string | '') => {
    const raw = value === '' || value == null ? '' : value;
    const count = raw === '' ? 0 : parseNonNegativeInteger(String(raw));
    const dateIso =
      (newMaterial.details as { dateRecorded?: string } | undefined)?.dateRecorded ||
      dayjs().format('YYYY-MM-DD');
    setInventorySegments(
      resizeWarehouseSegments(inventorySegments, Number.isFinite(count) ? count : 0, dateIso),
      raw
    );
  };

  // Helper function to check if pipe details are available in selected pipe
  const getAvailablePipeDetails = () => {
    if (!pipeSizesData || !pipeSizesData[currentPipeSizeIndex]) return null;
    const selectedPipe = pipeSizesData[currentPipeSizeIndex];
    return {
      pipeNRI: selectedPipe.pipeNRI || '',
      pipeNo: selectedPipe.pipeNo || '',
      pipeHeatNumber: selectedPipe.pipeHeatNumber || '',
      pipeLength: selectedPipe.pipeLength || '',
      pipeOuterDiameter: selectedPipe.pipeOuterDiameter || '',
      pipeWallThickness: selectedPipe.pipeWallThickness || ''
    };
  };

  // Helper function to format display values based on unit setting
  const formatDisplayValue = (value: any, field: string): string => {
    if (value === undefined || value === null || value === '') return '';

    // For numeric fields that need unit conversion, convert to display unit
    if (typeof value === 'number' && ['pipeLength', 'pipeOuterDiameter', 'pipeOuterDiameter2', 'pipeWallThickness', 'pipeWallThickness2'].includes(field)) {
      return convertSetupValue(value).toString();
    }

    return value.toString();
  };

  // Helper function to get raw value for saving (always in inches)
  const getRawValue = (value: any): any => {
    if (value === undefined || value === null || value === '') return value;
    return value;
  };

  // Function to manually refresh pipe details from selected pipe
  const refreshPipeDetails = () => {

  };

  // Handle pipe size selection with data restoration and pipe details update
  const handlePipeSizeChange = (value: string) => {
    const pipeIndex = parseInt(value);
    const selectedPipe = pipeSizesData[pipeIndex];

    setSelectedPipe(selectedPipe);

    // Update internal state
    setCurrentPipeSizeIndex(pipeIndex);

    // Notify parent component about pipe size change
    if (onPipeSizeChange) {
      onPipeSizeChange(pipeIndex);
    }

    if (pipeInventoryMode && selectedPipe?.pipeId != null) {
      setNewMaterial((prev) => ({
        ...prev,
        pipeId: Number(selectedPipe.pipeId) || prev.pipeId,
        details: {
          ...prev.details,
          pipePipeId: String(selectedPipe.pipeId),
        },
      }));
      setFitterPrefillOk(false);
      setFitterPrefillMessage(null);
    }
  };

  const GENERATED_PIPE_COUNT = 20;
   const startIndex = 1201

   const makePipes = (): MaterialData[] => {
    const list: MaterialData[] = [];
    const heats = [
      'J5K-5270',
      'J5K-5271',
      'J4K-5272',
      'J5K-5273',
      'J5K-5274',
      'J5K-5275',
      'J5K-5276',
      'J5K-5277',
      'J5K-5278',
      'J5K-5279',
      'J5K-5280',
      'J5K-5281',
    ];

    const forgeHeats = [
      '25HT0182',
      '25HT0183',
      '25HT0184',
      '25HT0185',
      '25HT0186',
      '25HT0187',
      '25HT0188',
      '25HT0189',
    ];
    const isForge = newMaterial.materialCategory === 'Forge';
    const manufacturerIds = selectedManufacturer.map((id) => Number(id));
    const forgeGradeIds = selectedPipeGrade.map((id) => Number(id));
    const materialTypeIds = selectedMaterialType.map((id) => Number(id));

    for (let index = 0; index < GENERATED_PIPE_COUNT; index++) {
      const pipeSeq = startIndex + index;
      const r = Math.sin(pipeSeq * 12.9898) * 43758.5453;
      const f = r - Math.floor(r);
      let clearance = 'PASS';

      if (f < 0.06) clearance = 'FAIL';
      else if (f < 0.16) clearance = 'PENDING_REVIEW';
      else if (f < 0.22) clearance = 'INCOMPLETE';

      const materialStatus = clearance === 'PASS' ? 'Available' : clearance === 'FAIL' ? 'Consumed' : 'Available';
      const heatIndex = index % heats.length;
      const heatNumber = heats[heatIndex];
      const lotNumber = `LOT-${300 + heatIndex}`;
      const workOrderNumber = `${40000 + pipeSeq}`;

      const baseMaterial = {
        pipeId: Number(selectedPipe.pipeId),
        projectTypeId: Number(selectedPipe.projectTypeId),
        projectType: selectedPipe.projectType,
        projectSubType: selectedPipe.projectSubType || 'none',
        materialCategory: (isForge ? 'Forge' : 'Pipe') as MaterialData['materialCategory'],
        materialStatus: materialStatus as MaterialData['materialStatus'],
        materialDate: new Date(2026, index % 12, (index % 28) + 1).toISOString(),
        assemblyNumbers: [],
        materialManufacturerId: isForge ? manufacturerIds : [],
        documents: [],
      };

      if (isForge) {
        list.push({
          ...baseMaterial,
          details: {
            forgeNRI: `NRI-U-${50000 + pipeSeq}`,
            forgeWorkOrderNumber: workOrderNumber,
            forgeOrderNumber: workOrderNumber,
            forgeSerialNumber: `Lot-25HT0182-${pipeSeq}`,
            forgeHeatNumber: '25HT0182' ,
            forgeBatchNumber: lotNumber,
            forgeLength: 480 + (index % 40),
            forgeWallThickness: 10 + (index % 10),
            forgeProjectStage: 'Production',
            forgeGrade: forgeGradeIds,
            pipeMaterialType: materialTypeIds,
            manufacturerIds,
          },
        });
        continue;
      }

      list.push({
        ...baseMaterial,
        details: {
          pipeNRI: `NRI-U-${20000 + pipeSeq}`,
          pipeNo: `${40000 + pipeSeq}`,
          pipePipeId: `PIPE-${pipeSeq}`,
          lotNumber,
          pipeHeatNumber: heatNumber,
          pipeLength: 480 + (index % 40),
          pipeProjectStage: index % 5 === 0 ? 'PQT' : 'Production',
          pipeExtraDetails: {
            pipeLotNumber: lotNumber,
            pipeData: {
              lotId: lotNumber,
            },
          },
        },
      });
    }

    return list;
  }


const updateNewPipesToDatabase = async () => {
  if (isUpdatingPipes) return;
  const isForge = newMaterial.materialCategory === 'Forge';
  const materialLabel = isForge ? 'forges' : 'pipes';
  if (!selectedPipe?.pipeId) {
    Notification.warning('Pipe size is missing. Please select a pipe size and try again.');
    return;
  }
  if (!selectedPipe?.projectId && !project?.project?.projectId) {
    Notification.warning('Project is missing. Please select a pipe size and try again.');
    return;
  }

  try {
    setIsUpdatingPipes(true);
    const newMaterials = makePipes();
    const apiPayload = {
      data: newMaterials,
      projectId: Number(selectedPipe?.projectId || project.project.projectId),
    };

    const result = await AddMaterial(apiPayload, false);
    if (result) {
      await onAdd(result);
      Notification.success(`${isForge ? 'Forges' : 'Pipes'} updated`, `${newMaterials.length} ${materialLabel} saved to database.`);
      onClose();
      resetForm();
    }
  } catch (error) {
    Notification.warning(`Unable to update ${materialLabel}. Please try again.`);
  } finally {
    setIsUpdatingPipes(false);
  }
}


  // Optimized handleAddMaterial function
  const handleAddMaterial = async () => {
    if (isSubmitting) {
      return;
    }


    try {
      // Basic validation
      if (!newMaterial.materialCategory) {
        Notification.warning('Please select a material category');
        return;
      }

      // Validate required fields based on category
      const validationError = validateMaterialFields(newMaterial.materialCategory, newMaterial);
      if (validationError) {
        Notification.warning('Please fill  the required fields', validationError);
        return;
      }

      if (newMaterial.materialCategory === 'Misc' && !newMaterial.details?.miscMaterialTypeName?.trim()) {
        Notification.warning('Please select a misc material type');
        return;
      }

      // Validate pipe selection
      if (!pipeSizesData || !pipeSizesData[currentPipeSizeIndex]) {
        Notification.warning('Please select a valid pipe size');
        return;
      }

      // // Validate document upload for Pipe and Forge categories
      // if ((newMaterial.category === 'Pipe' || newMaterial.category === 'Forge') && materialDocuments.length === 0) {
      //   Notification.warning(`Please upload  document for ${newMaterial.category} materials`);
      //   return;
      // }

      setIsSubmitting(true);
      const selectedPipe = pipeSizesData[currentPipeSizeIndex];

      // Prepare payload for S3 upload
      const payload = {
        projectId: Number(selectedPipe.projectId),
        pipeId: Number(selectedPipe.pipeId),
        projectTypeId: Number(selectedPipe.projectTypeId),
        materialCategory: newMaterial.materialCategory
      };

      // Upload documents to S3 if any
      let s3Response: any[] = [];
      if (materialDocuments.length > 0) {
        try {
          s3Response = await getPreSignedUrlToUpload(materialDocuments, payload);
        } catch (error) {
          Notification.warning('Failed to upload documents. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      // Prepare material data
      const materialData = [{
        projectTypeId: String(selectedPipe.projectTypeId),
        projectType: selectedPipe.projectType,
        projectSubType: selectedPipe.projectSubType || "none",
        materialCategory: newMaterial.materialCategory,
        materialStatus: newMaterial.materialStatus || "Available",
        pipeId: selectedPipe.pipeId,
        assemblyNumbers: [],
        materialDate: date,
        materialManufacturerId: selectedManufacturer.map(id => Number(id)),

        documents: s3Response.map((file: any, index: number) => ({
          fileName: file.fileName,
          s3Key: file.s3Key,
          version: 1,
          tags: selectedTags[index],
          uploadedBy: user.email || ''
        })),
        details: {
          ...newMaterial.details,

        }
      }];


      if (isPipeLikeMaterialCategory(newMaterial.materialCategory)) {
        const rawPipeNri = materialData[0].details?.pipeNRI;
        materialData[0].details.pipeNRI =
          rawPipeNri == null || String(rawPipeNri).trim() === '' ? null : String(rawPipeNri).trim();
        // Convert string IDs back to numbers for API
        materialData[0].details.pipeGrade = selectedPipeGrade.map(id => Number(id));
        materialData[0].details.pipeMaterialType = selectedMaterialType.map(id => Number(id));
        materialData[0].details.manufacturerIds = selectedManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedManufacturer.map(id => Number(id));
        const pipeManufacturerLabel = selectedManufacturer
          .map((id: string) => manufacturerOptions.find((o: any) => o.value === id)?.label)
          .filter(Boolean)
          .join(', ');
        if (pipeManufacturerLabel) {
          materialData[0].details.manufacturerLabel = pipeManufacturerLabel;
        }
        // Convert length fields from feet to inches for storage
        materialData[0].details.pipeLength = newMaterial.details?.pipeLength ? convertValueToInches(Number(newMaterial.details.pipeLength), materialUnit.pipeLength) : undefined;
        materialData[0].details.pipeOuterDiameter = newMaterial.details?.pipeOuterDiameter ? convertValueToInches(Number(newMaterial.details.pipeOuterDiameter), materialUnit.pipeOuterDiameter) : undefined;
        materialData[0].details.pipeOuterDiameter2 = newMaterial.details?.pipeOuterDiameter2 ? convertValueToInches(Number(newMaterial.details.pipeOuterDiameter2), materialUnit.pipeOuterDiameter) : undefined;
        materialData[0].details.pipeWallThickness = newMaterial.details?.pipeWallThickness ? convertValueToInches(Number(newMaterial.details.pipeWallThickness), materialUnit.pipeWallThickness) : undefined;
        materialData[0].details.pipeWallThickness2 = newMaterial.details?.pipeWallThickness2 ? convertValueToInches(Number(newMaterial.details.pipeWallThickness2), materialUnit.pipeWallThickness) : undefined;
        // Add pipe-specific fields directly to details
        // materialData[0].details.pipeWorkOrderNumber = newMaterial.details?.pipeExtraDetails?.pipeWorkOrderNumber;
        // materialData[0].details.pipePurchaseOrderNumber = newMaterial.details?.pipeExtraDetails?.pipePurchaseOrderNumber;
        // materialData[0].details.pipeLocation = newMaterial.details?.pipeExtraDetails?.pipeLocation;
        // materialData[0].details.pipeProjectStage = newMaterial.details?.pipeProjectStage;
        // materialData[0].details.pipeMarking = newMaterial.details?.pipeMarking;
        // materialData[0].details.pipeNotes = newMaterial.details?.pipeNotes;
        materialData[0].details.pipeLengthRemaining = newMaterial.details?.pipeLengthRemaining ? convertValueToInches(Number(newMaterial.details.pipeLengthRemaining), materialUnit.pipeLengthRemaining) : undefined;
        materialData[0].details.pipeWeightPerFt = newMaterial.details?.pipeWeightPerFt ? Number(newMaterial.details.pipeWeightPerFt) : undefined;

        // Add pipe-specific extra details with proper unit conversions
        materialData[0].details.pipeExtraDetails = {
          ...(newMaterial.details?.pipeExtraDetails || {}),
          pipeLotNumber: newMaterial.details?.pipeLotNumber || undefined,
          pipeType: newMaterial.details?.pipeType || undefined,
          pipePslLevel: newMaterial.details?.pipePslLevel || undefined,
          pipeDelivery: newMaterial.details?.pipeDelivery || undefined,
          pipeLengthRemaining: newMaterial.details?.pipeLengthRemaining ? convertValueToInches(Number(newMaterial.details.pipeLengthRemaining), materialUnit.pipeLengthRemaining) : undefined,
          pipeWeightPerFt: newMaterial.details?.pipeWeightPerFt ? Number(newMaterial.details.pipeWeightPerFt) : undefined,
          wpsNumber: newMaterial.details?.pipeExtraDetails?.wpsNumber?.trim() || undefined,
          contractor: newMaterial.details?.pipeExtraDetails?.contractor?.trim() || undefined,
          manufacturerLabel: pipeManufacturerLabel || undefined,
          numberOfPups:
            newMaterial.details?.pipeExtraDetails?.numberOfPups != null &&
            newMaterial.details.pipeExtraDetails.numberOfPups !== ''
              ? parseNonNegativeInteger(
                  String(newMaterial.details.pipeExtraDetails.numberOfPups)
                )
              : undefined,
        };
        if (pipeInventoryMode) {
          const detailsAny = materialData[0].details as Record<string, unknown>;
          const dateRecorded = (newMaterial.details as { dateRecorded?: string } | undefined)
            ?.dateRecorded;
          detailsAny.mtrReceived = Boolean(
            (newMaterial.details as { mtrReceived?: boolean } | undefined)?.mtrReceived
          );
          detailsAny.dateRecorded = dateRecorded
            ? new Date(dateRecorded).toISOString()
            : null;
          detailsAny.pipeSegmentation =
            inventorySegments.length > 0
              ? inventorySegments.map((seg) => ({
                  id: seg.id,
                  segmentName: seg.segmentName,
                  segmentLength: parseNonNegativeNumber(String(seg.segmentLength)),
                  dateInventory: seg.dateInventory
                    ? new Date(seg.dateInventory).toISOString()
                    : new Date().toISOString(),
                  used: seg.used,
                }))
              : undefined;
          if (materialData[0].details.pipeLength != null) {
            materialData[0].details.pipeLength = parseNonNegativeNumber(
              String(materialData[0].details.pipeLength)
            );
          }
        }
        (materialData[0] as any).units = {
          pipeOuterDiameter: materialUnit.pipeOuterDiameter,
          pipeOuterDiameter2: materialUnit.pipeOuterDiameter,
          pipeWallThickness: materialUnit.pipeWallThickness,
          pipeWallThickness2: materialUnit.pipeWallThickness,
          pipeLength: materialUnit.pipeLength,
          pipeLengthRemaining: materialUnit.pipeLengthRemaining
        };

      }
      if (newMaterial.materialCategory === 'Forge') {
        // Convert string IDs back to numbers for API
        materialData[0].details.forgeGrade = selectedPipeGrade.map(id => Number(id)); // Using pipeGrade for forge grade
        materialData[0].details.pipeMaterialType = selectedMaterialType.map(id => Number(id));
        materialData[0].details.manufacturerIds = selectedManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedManufacturer.map(id => Number(id));

        // Add forge-specific fields directly to details

        materialData[0].details.forgeLength = newMaterial.details?.forgeLength ? convertValueToInches(Number(newMaterial.details.forgeLength), 'ft') : undefined;
        materialData[0].details.forgeWallThickness = newMaterial.details?.forgeWallThickness ? convertValueToInches(Number(newMaterial.details.forgeWallThickness), unit) : undefined;
        materialData[0].details.forgeLengthRemaining = newMaterial.details?.forgeLengthRemaining ? convertValueToInches(Number(newMaterial.details.forgeLengthRemaining), 'ft') : undefined;
        materialData[0].details.forgeWeightPerFt = newMaterial.details?.forgeWeightPerFt ? Number(newMaterial.details.forgeWeightPerFt) : undefined;
        // Add forge-specific extra details with proper unit conversions
        materialData[0].details.forgeExtraDetails = {
          forgeLengthRemaining: newMaterial.details?.forgeLengthRemaining ? convertValueToInches(Number(newMaterial.details.forgeLengthRemaining), 'ft') : undefined,
          forgeWeightPerFt: newMaterial.details?.forgeWeightPerFt ? Number(newMaterial.details.forgeWeightPerFt) : undefined
        };
        (materialData[0] as any).units = {
          forgeOuterDiameter: unit, forgeWallThickness: unit,
          forgeLength: 'ft', forgeLengthRemaining: 'ft'
        };
      }

      else if (newMaterial.materialCategory === 'Coating') {
        // Add manufacturer IDs for Coating
        materialData[0].details.manufacturerIds = selectedCoatingManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedCoatingManufacturer.map(id => Number(id));

      } else if (newMaterial.materialCategory === 'Gas') {
        // Add manufacturer IDs for Gas
        materialData[0].details.manufacturerIds = selectedGasManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedGasManufacturer.map(id => Number(id));
      } else if (newMaterial.materialCategory === 'Wire Lot') {
        // Add manufacturer IDs for Wire Lot
        materialData[0].details.manufacturerIds = selectedWireManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedWireManufacturer.map(id => Number(id));
      } else if (newMaterial.materialCategory === 'NDE') {
        // Add manufacturer IDs for NDE
        materialData[0].details.manufacturerIds = selectedNDEManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedNDEManufacturer.map(id => Number(id));
        // Get manufacturer labels from manufacturerOptions
        const manufacturerLabels = selectedNDEManufacturer.map((id: string) => {
          const option = manufacturerOptions.find((opt: any) => opt.value === id);
          return option ? option.label : '';
        }).filter((label: string) => label !== '');
        // Save manufacturer labels and materialId to ndeExtraDetails
        materialData[0].details.ndeExtraDetails = {
          ...materialData[0].details.ndeExtraDetails,
          ndeManufacturer: manufacturerLabels.join(', '),
          ndeMaterialId: ndeMaterialId || materialData[0].details.ndeExtraDetails?.ndeMaterialId
        };
      } else if (newMaterial.materialCategory === 'Misc') {
        const typeName = newMaterial.details?.miscMaterialTypeName?.trim() || '';
        const opt =
          miscMaterialTypeOptions.find((o: any) => o.label === typeName) ??
          materialTypeOptions.find((o: any) => o.label === typeName);
        const typeIds = opt ? [Number(opt.value)] : [];
        materialData[0].details.miscMaterialType = typeIds;
        materialData[0].details.miscMaterialTypeLabels = typeName;
        materialData[0].details.miscMaterialTypeName = typeName;
        if (typeIds.length) {
          materialData[0].details.miscMaterialTypeIds = typeIds;
        }
        materialData[0].details.manufacturerIds = selectedManufacturer.map((id) => Number(id));
        materialData[0].materialManufacturerId = selectedManufacturer.map((id) => Number(id));
        const manuLabels = selectedManufacturer
          .map((id: string) => manufacturerOptions.find((o: any) => o.value === id)?.label)
          .filter(Boolean)
          .join(', ');
        materialData[0].details.miscExtraDetails = {
          ...materialData[0].details.miscExtraDetails,
          miscManufacturer: manuLabels,
        };
        materialData[0].details.miscQuantity =
          newMaterial.details?.miscQuantity !== undefined && newMaterial.details?.miscQuantity !== ''
            ? Number(newMaterial.details.miscQuantity)
            : undefined;
      }

      const apiPayload = {
        data: materialData,
        projectId: selectedPipe.projectId
      }

      const result = await AddMaterial(apiPayload, false);

      if (result) {
        if (pipeInventoryMode) {
          logPipeInventoryMaterialAdded(result as MaterialData);
        }
        await onAdd(result);
        onClose();
        resetForm();
      }
      //  else {
      //   Notification.warning('Failed to add material. Please try again.');
      // }

    } catch (error) {
      Notification.warning('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddTag = async (tagName: string) => {
    if (tagName.trim()) {
      if (getTags(newMaterial.materialCategory || 'Pipe').find((tag: any) => tag.tags_name === tagName.trim())) {
        Notification.warning(`${tagName.trim()} tag already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: tagName.trim(),
        project_id: selectedPipe.projectId,
        tags_type: isPipeLikeMaterialCategory(newMaterial.materialCategory) ? 'MATERIAL_PIPE_TAGS' : newMaterial.materialCategory === 'Forge' ? 'MATERIAL_FORGE_TAGS' : newMaterial.materialCategory === 'Gas' ? 'MATERIAL_GAS_TAGS' : newMaterial.materialCategory === 'Wire Lot' ? 'MATERIAL_WIRE_LOT_TAGS' : newMaterial.materialCategory === 'NDE' ? 'MATERIAL_NDE_TAGS' : newMaterial.materialCategory === 'Misc' ? 'MATERIAL_MISC_TAGS' : 'MATERIAL_COATING_TAGS',
        created_by: user.email || ''
      }
      const res = await AddMaterialCategory(apiPayload,
        isPipeLikeMaterialCategory(newMaterial.materialCategory) ? 'Pipe Tag' : newMaterial.materialCategory === 'Forge' ? 'Forge Tag' : newMaterial.materialCategory === 'Gas' ? 'Gas Tag' : newMaterial.materialCategory === 'Wire Lot' ? 'Wire Lot Tag' : newMaterial.materialCategory === 'NDE' ? 'NDE Tag' : newMaterial.materialCategory === 'Misc' ? 'Misc Tag' : 'Coating Tag');
      if (res) {

        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);
        // if (materialDocuments.length > 0) {
        //   let updatedTags = [...selectedTags];
        //   updatedTags[0] = [...updatedTags[0], res.tags_id.toString()];

        //   setSelectedTags(updatedTags as any);
        // }
        setIsAddingTag(false);
        //  setSelectedTags(prev => [...prev, res.tags_id.toString()]);
        // Automatically select the newly added tag
        // setSelectedTags(prev => [...prev, tagName.trim()]);
      }
    }
  };


  const handleAddPipeGrade = async (gradeName: string) => {
    if (gradeName.trim()) {
      if (pipeGradeOptions.find((tag: any) => tag.label === gradeName.trim())) {
        Notification.warning(`${gradeName.trim()} grade already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: gradeName.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_PIPE_GRADE',
        created_by: user.email || 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Material pipe grade Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);
        // Automatically select the newly added tag
        setSelectedPipeGrade(prev => [...prev, res.tags_id.toString()]);

      }
    }
  }



  const handleAddMaterialType = async (typeName: string) => {
    if (typeName.trim()) {
      if (materialTypeOptions.find((tag: any) => tag.label === typeName.trim())) {
        Notification.warning(`${typeName.trim()} material type already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: typeName.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_TYPE_TAGS',
        created_by: user.email || 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Material Type Tag');
      if (res) {
        setDocumentTags([...documentTags, res]);
        setSelectedMaterialType((prev) => [...prev, res.tags_id.toString()]);
      }
    }
  }

  const handleAddMiscMaterialType = async (typeName: string) => {
    if (!typeName.trim() || !pipeSizesData[currentPipeSizeIndex]) return;
    if (miscMaterialTypeOptions.find((tag: any) => tag.label === typeName.trim())) {
      Notification.warning(`${typeName.trim()} already exists`);
      setIsAddingMiscMaterialType(false);
      return;
    }
    const apiPayload = {
      tags_name: typeName.trim(),
      project_id: pipeSizesData[currentPipeSizeIndex].projectId,
      tags_type: 'MATERIAL_MISC_MATERIAL_TYPE_TAGS',
      created_by: user.email || 'admin',
    };
    const res = await AddMaterialCategory(apiPayload, 'Misc material type');
    if (res) {
      const updatedLocalTags = [...localDocumentTags, res];
      setLocalDocumentTags(updatedLocalTags);
      setDocumentTags(updatedLocalTags);
      setNewMaterial((prev) => ({
        ...prev,
        details: {
          ...prev.details,
          miscMaterialTypeName: typeName.trim(),
          miscMaterialTypeIds: [res.tags_id],
        },
      }));
    }
    setIsAddingMiscMaterialType(false);
  };

  const handleAddManufacturer = async (manufacturerName: string) => {
    if (manufacturerName.trim()) {
      if (manufacturerTagOptions.find((tag: any) => tag.label === manufacturerName.trim())) {
        Notification.warning(`${manufacturerName.trim()} manufacturer already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: manufacturerName.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_MANUFACTURER_TAGS',
        created_by: user.email || 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Manufacturer Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);

        // Automatically select the newly added manufacturer based on current material category
        if (newMaterial.materialCategory === 'Coating') {
          setSelectedCoatingManufacturer(prev => [...prev, res.tags_id.toString()]);
        } else if (newMaterial.materialCategory === 'Gas') {
          setSelectedGasManufacturer(prev => [...prev, res.tags_id.toString()]);
        } else if (newMaterial.materialCategory === 'Wire Lot') {
          setSelectedWireManufacturer(prev => [...prev, res.tags_id.toString()]);
        } else if (newMaterial.materialCategory === 'NDE') {
          setSelectedNDEManufacturer(prev => [...prev, res.tags_id.toString()]);
        } else if (isPipeLikeMaterialCategory(newMaterial.materialCategory)) {
          setSelectedManufacturer(prev => [...prev, res.tags_id.toString()]);
        } else if (newMaterial.materialCategory === 'Forge') {
          setSelectedManufacturer(prev => [...prev, res.tags_id.toString()]);
        } else if (newMaterial.materialCategory === 'Misc') {
          setSelectedManufacturer(prev => [...prev, res.tags_id.toString()]);
        }
      }
    }
  }

  const handleAddWorkOrderNumber = async (workOrderNumber: string) => {
    if (workOrderNumber.trim()) {
      if (workOrderNumberOptions.find((tag: any) => tag.label === workOrderNumber.trim())) {
        Notification.warning(`${workOrderNumber.trim()} work order number already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: workOrderNumber.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_WORK_ORDER_NUMBER_TAGS',
        created_by: user.email || 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Work Order Number Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);

        // Update the material details with the new work order number
        if (isPipeLikeMaterialCategory(newMaterial.materialCategory)) {
          // Update the material details with the new work order number
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              pipeWorkOrderNumber: workOrderNumber.trim(),
              // Also update forge fields if this is a forge material

            }
          }))
        }
        else if (newMaterial.materialCategory === 'Misc') {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              miscJobNumber: workOrderNumber.trim(),
            }
          }))
        }
        else {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              forgeWorkOrderNumber: workOrderNumber.trim(),

            }
          }))
        }
      }
    }
    setIsAddingWorkOrderNumber(false);

  }

  const handleAddPONumber = async (poNumber: string) => {
    if (poNumber.trim()) {
      if (poNumberOptions.find((tag: any) => tag.label === poNumber.trim())) {
        Notification.warning(`${poNumber.trim()} PO number already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: poNumber.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_PO_NUMBER_TAGS',
        created_by: user.email || 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Purchase Order Number Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);

        // Update the material details with the new PO number
        if (isPipeLikeMaterialCategory(newMaterial.materialCategory)) {
          // Update the material details with the new PO number
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              pipePurchaseOrderNumber: poNumber.trim(),

            }
          }))
        }
        else if (newMaterial.materialCategory === 'Misc') {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              miscPoNumber: poNumber.trim(),
            }
          }))
        }
        else {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              forgePurchaseOrderNumber: poNumber.trim(),

            }
          }))
        }
      }
    }
    setIsAddingPONumber(false);
  }

  const handleAddLocation = async (location: string) => {
    if (location.trim()) {
      if (locationOptions.find((tag: any) => tag.label === location.trim())) {
        Notification.warning(`${location.trim()} location already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: location.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_LOCATION_TAGS',
        created_by: user.email || 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Location Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);

        if (newMaterial.materialCategory === 'Forge') {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              forgeLocation: location.trim(),
            }
          }))
        } else if (newMaterial.materialCategory === 'Misc') {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              miscLocation: location.trim(),
            }
          }))
        } else {
          setNewMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              pipeLocation: location.trim(),
            }
          }))
        }
      }
    }
    setIsAddingLocation(false);
  }

  const handleAddBasePipeOption = async (
    name: string,
    tagsType: string,
    existingOptions: { value: string; label: string }[],
    detailKey: 'pipeType' | 'pipePslLevel' | 'pipeDelivery',
    closeDialog: () => void,
    label: string,
  ) => {
    const trimmed = name.trim();
    if (!trimmed || !pipeSizesData[currentPipeSizeIndex]) return;
    if (existingOptions.find((tag: any) => tag.label === trimmed)) {
      Notification.warning(`${trimmed} already exists`);
      closeDialog();
      return;
    }

    const apiPayload = {
      tags_name: trimmed,
      project_id: pipeSizesData[currentPipeSizeIndex].projectId,
      tags_type: tagsType,
      created_by: user.email || 'admin',
    };
    const res = await AddMaterialCategory(apiPayload, label);
    if (res) {
      const updatedLocalTags = [...localDocumentTags, res];
      setLocalDocumentTags(updatedLocalTags);
      setDocumentTags(updatedLocalTags);
      setNewMaterial((prev) => ({
        ...prev,
        details: { ...prev.details, [detailKey]: trimmed },
      }));
    }
    closeDialog();
  };

  const handleAddPipeType = (name: string) =>
    handleAddBasePipeOption(name, 'MATERIAL_PIPE_TYPE_TAGS', pipeTypeOptions, 'pipeType', () => setIsAddingPipeType(false), 'Pipe type');

  const handleAddPipePslLevel = (name: string) =>
    handleAddBasePipeOption(name, 'MATERIAL_PIPE_PSL_LEVEL_TAGS', pipePslLevelOptions, 'pipePslLevel', () => setIsAddingPipePslLevel(false), 'Pipe PSL level');

  const handleAddPipeDelivery = (name: string) =>
    handleAddBasePipeOption(name, 'MATERIAL_PIPE_DELIVERY_TAGS', pipeDeliveryOptions, 'pipeDelivery', () => setIsAddingPipeDelivery(false), 'Pipe delivery');

  const handleAddMiscLotNumber = async (name: string) => {
    if (!name.trim() || !pipeSizesData[currentPipeSizeIndex]) return;
    if (miscLotNumberTagOptions.find((tag: any) => tag.label === name.trim())) {
      Notification.warning(`${name.trim()} already exists`);
      setIsAddingMiscLot(false);
      return;
    }
    const apiPayload = {
      tags_name: name.trim(),
      project_id: pipeSizesData[currentPipeSizeIndex].projectId,
      tags_type: 'MATERIAL_MISC_LOT_NUMBER_TAGS',
      created_by: user.email || 'admin',
    };
    const res = await AddMaterialCategory(apiPayload, 'Misc lot number');
    if (res) {
      const updatedLocalTags = [...localDocumentTags, res];
      setLocalDocumentTags(updatedLocalTags);
      setDocumentTags(updatedLocalTags);
      setNewMaterial((prev) => ({
        ...prev,
        details: { ...prev.details, miscLotNumber: name.trim() },
      }));
    }
    setIsAddingMiscLot(false);
  };

  const resetForm = () => {
    // Reset to initial state with proper pipe data if available
    const initialProjectTypeId = project.project.projectTypes.pipeline?.project_type_id ||
      project.project.projectTypes.fabrication?.project_type_id ||
      project.project.projectTypes.others?.project_type_id || 98;
    const initialProjectType = project.project.projectTypes.pipeline?.enabled ? 'pipeline' :
      project.project.projectTypes.fabrication?.enabled ? 'fabrication' : 'others';

    // If we have pipe data, use it for restoration
    if (pipeSizesData && pipeSizesData[currentPipeSizeIndex]) {
      const selectedPipe = pipeSizesData[currentPipeSizeIndex];
      setNewMaterial({
        materialCategory: 'Pipe',
        pipeId: Number(selectedPipe.pipeId),
        materialStatus: 'Available',
        projectTypeId: Number(selectedPipe.projectTypeId),
        projectType: selectedPipe.projectType,
        projectSubType: selectedPipe.projectSubType || 'none',
        details: {
          // Keep existing details but ensure pipe measurements are properly formatted
          ...newMaterial.details,
          pipeLength: selectedPipe.pipeLength || undefined,
          pipeOuterDiameter: selectedPipe.pipeOuterDiameter || undefined,
          pipeWallThickness: selectedPipe.pipeWallThickness || undefined
        }
      });
    } else {
      // Fallback to default values
      setNewMaterial({
        materialCategory: 'Pipe',
        pipeId: 0,
        materialStatus: 'Available',
        projectTypeId: initialProjectTypeId,
        projectType: initialProjectType,
        projectSubType: 'none',
        details: {}
      });
    }

    setSelectedTags([]);
    setSelectedCertifications([]);
    setMaterialDocuments([]);
    setSelectedMaterialType([]);
    setSelectedManufacturer([]);
    setSelectedPipeGrade([]);
    setSelectedProjectStage([]);
    setSelectedCoatingManufacturer([]);
    setSelectedGasManufacturer([]);
    setSelectedWireManufacturer([]);
    setSelectedNDEManufacturer([]);
    setNdeExpDate('');
    setNdeMaterialId('');
    setNdeSelectedLocation('');
    setNdeNotes('');
    setIsAddingTag(false);
    setNewTag('');
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };
  const getTags = (category: any) => {
    if (isPipeLikeMaterialCategory(category)) {

      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_TAGS');
    }
    else if (category === 'Forge') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_FORGE_TAGS');
    }
    else if (category === 'Gas') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_GAS_TAGS');
    }
    else if (category === 'Wire Lot') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_WIRE_LOT_TAGS');
    }
    else if (category === 'Coating') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_COATING_TAGS');
    }
    else if (category === 'Misc') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_MISC_TAGS');
    }
    else if (category === 'Pipe Grade') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_GRADE');
    }
    else if (category === 'Material Type') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_TYPE_TAGS');
    }
    else if (category === 'Manufacturer') {
      return localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_MANUFACTURER_TAGS');
    }
    return [];
  }

  // Convert document tags to options for the MultiSelect component
  // value: string representation of tag ID, label: tag name
  const tagOptions = useMemo(() =>
    getTags(newMaterial.materialCategory || 'Pipe').map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    })), [localDocumentTags, newMaterial.materialCategory]
  );


  // Convert document tags to options for the MultiSelect component
  // value: string representation of tag ID, label: tag name
  const materialTypeOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_TYPE_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  // Convert manufacturers to options for the MultiSelect component
  // Note: This uses manufacturers from selectedPipe, not from documentTags
  // value: string representation of manufacturer ID, label: manufacturer name
  const manufacturerOptions = useMemo(() =>
    selectedPipe && selectedPipe.manufacturers
      ? selectedPipe.manufacturers.map((manufacturer: any) => ({
        value: manufacturer.manufacturerId.toString(),
        label: manufacturer.manufacturerName
      }))
      : [], [selectedPipe]
  );

  // Convert document tags to options for manufacturer MultiSelect components
  // This is used for Coating, Gas, and Wire Lot categories
  const manufacturerTagOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_MANUFACTURER_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  // Convert document tags to options for the MultiSelect component
  // value: string representation of tag ID, label: tag name
  const pipeGradeOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_GRADE')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );
  const workOrderNumberOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_WORK_ORDER_NUMBER_TAGS' && tag.project_id === selectedPipe.projectId)
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );



  const poNumberOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_PO_NUMBER_TAGS' && tag.project_id === selectedPipe.projectId)
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  const locationOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_LOCATION_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  const miscTagProjectId = pipeSizesData[currentPipeSizeIndex]?.projectId;

  const mergeBasePipeOptions = useCallback((defaults: string[], tags: { value: string; label: string }[]) => {
    const seen = new Set<string>();
    return [
      ...defaults.map((label) => ({ value: label, label })),
      ...tags,
    ].filter((option) => {
      const key = option.label.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const pipeTypeOptions = useMemo(
    () => {
      const tagOptions = localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_TYPE_TAGS' && tag.project_id === miscTagProjectId)
        .map((tag: any) => ({ value: tag.tags_name, label: tag.tags_name }));
      return mergeBasePipeOptions(['SAWL', 'SMLS', 'ERW'], tagOptions);
    },
    [localDocumentTags, miscTagProjectId, mergeBasePipeOptions]
  );

  const pipePslLevelOptions = useMemo(
    () => {
      const tagOptions = localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_PSL_LEVEL_TAGS' && tag.project_id === miscTagProjectId)
        .map((tag: any) => ({ value: tag.tags_name, label: tag.tags_name }));
      return mergeBasePipeOptions(['PSL1', 'PSL2'], tagOptions);
    },
    [localDocumentTags, miscTagProjectId, mergeBasePipeOptions]
  );

  const pipeDeliveryOptions = useMemo(
    () => {
      const tagOptions = localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_DELIVERY_TAGS' && tag.project_id === miscTagProjectId)
        .map((tag: any) => ({ value: tag.tags_name, label: tag.tags_name }));
      return mergeBasePipeOptions(['TMCP', 'Normalized', 'Quenched and Tempered'], tagOptions);
    },
    [localDocumentTags, miscTagProjectId, mergeBasePipeOptions]
  );

  const miscLotNumberTagOptions = useMemo(
    () =>
      localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_MISC_LOT_NUMBER_TAGS' && tag.project_id === miscTagProjectId)
        .map((tag: any) => ({ value: tag.tags_id.toString(), label: tag.tags_name })),
    [localDocumentTags, miscTagProjectId]
  );

  const miscMaterialTypeOptions = useMemo(
    () =>
      localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_MISC_MATERIAL_TYPE_TAGS' && tag.project_id === miscTagProjectId)
        .map((tag: any) => ({ value: tag.tags_id.toString(), label: tag.tags_name })),
    [localDocumentTags, miscTagProjectId]
  );

  /** Misc types from new tag bucket, plus legacy MATERIAL_TYPE_TAGS if current label matches */
  const miscMaterialTypeSelectOptions = useMemo(() => {
    const merged = [...miscMaterialTypeOptions];
    const haveLabel = new Set(merged.map((o) => o.label));
    const selName = newMaterial.details?.miscMaterialTypeName?.trim();
    if (selName && !haveLabel.has(selName)) {
      const fallback = materialTypeOptions.find((o) => o.label === selName);
      if (fallback) {
        merged.push(fallback);
      }
    }
    return merged;
  }, [miscMaterialTypeOptions, materialTypeOptions, newMaterial.details?.miscMaterialTypeName]);

  const handleMaterialFileSelect = (selectedFiles: FileList | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);

      // Validate file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/jpeg',
        'image/png',
        'image/gif'
      ];

      const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.jpg', '.jpeg', '.png', '.gif'];

      const maxFileSize = MAX_UPLOAD_FILE_SIZE_BYTES;

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      const oversizedFiles: string[] = [];

      fileArray.forEach(file => {
        // Check file size first
        if (file.size > maxFileSize) {
          oversizedFiles.push(file.name);
          return;
        }

        // Check MIME type and extension
        const isValidType = allowedTypes.includes(file.type) ||
          allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

        if (isValidType) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      // Show notifications for various validation failures
      if (oversizedFiles.length > 0) {
        Notification.warning(
          'File too large',
          `The following files exceed the maximum size limit (${MAX_UPLOAD_FILE_SIZE_LABEL}): ${oversizedFiles.join(', ')}. Please compress or split these files.`
        );
      }

      if (invalidFiles.length > 0) {
        Notification.warning(
          'File type not allowed',
          `The following files cannot be uploaded due to security restrictions: ${invalidFiles.join(', ')}. Only PDF, Word, Excel, text, and image files are allowed.`
        );
      }

      // Add only valid files
      if (validFiles.length > 0) {
        const updatedFiles = [...materialDocuments, ...validFiles];
        setMaterialDocuments(updatedFiles);

        if (validFiles.length < fileArray.length) {
          Notification.info(
            'Files uploaded',
            `Successfully uploaded ${validFiles.length} valid file(s). Some files were rejected due to validation rules.`
          );
        }
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = materialDocuments.filter((_, i) => i !== index);
    setMaterialDocuments(updatedFiles);
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updatedTags = [...selectedTags];
    updatedTags[fileIndex] = tags;
    setSelectedIndex(fileIndex);
    setSelectedTags(updatedTags as any);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleCancel();
      }
    }}>
      <DialogContent className="max-w-7xl flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Material</DialogTitle>
        </DialogHeader>
        <div className="p-2">
          <div className="grid  p-2 grid-cols-4 gap-4 min-w-0  max-h-[75vh] flex flex-col min-h-[400px] overflow-y-auto    overflow-x-hidden">

            <div className="space-y-2">
              <Label>Pipe Size <span className="text-red-500">*</span></Label>
              <Select
                value={currentPipeSizeIndex.toString()}
                onValueChange={handlePipeSizeChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select pipe size" />
                </SelectTrigger>
                <SelectContent>
                  {pipeSizesData && pipeSizesData.map((pipe, index) => {
                    return (
                      <SelectItem key={index} value={index.toString()}>
                        {pipe.displayName}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

            </div>

            {!pipeInventoryMode && (
              <div className="space-y-2">
                <Label>Material Category <span className="text-red-500">*</span></Label>
                <Select
                  value={newMaterial.materialCategory}
                  onValueChange={(value) => {
                    setNewMaterial((prev) => ({ ...prev, materialCategory: value as MaterialData['materialCategory'] }));
                    setSelectedTags([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select material category" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialOptions && materialOptions.length > 0 && materialOptions.map((option, index) => (
                      <SelectItem key={index} value={option}>{option}</SelectItem>
                    ))}
                    {materialOptions && materialOptions.length === 0 && (
                      <SelectItem value="no-pipes-available" disabled>
                        No pipes available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">Date</Label>
              <div className="flex items-center space-x-2">
                <AntdDate
                  name="date"
                  value={newMaterial.details?.date || date}
                  onChange={(value: string) => {
                    setDate(value);
                    if (newMaterial) {
                      newMaterial.details.date = value;
                    }
                  }}
                />

              </div>
            </div>

            {/* <div className="space-y-2">
              <Label>Status </Label>
              <Select
                value={newMaterial.status || 'Available'}
                onValueChange={(value: 'Available' | 'Consumed') =>
                  setNewMaterial(prev => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Consumed">Consumed</SelectItem>
                </SelectContent>
              </Select>
            </div> */}

            {/* Dynamic fields based on category */}
            {isPipeLikeMaterialCategory(newMaterial.materialCategory) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="pipeNRI">Pipe NRI</Label>
                  <Input
                    id="pipeNRI"
                    value={newMaterial.details?.pipeNRI || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeNRI: e.target.value }
                    }))}
                    placeholder="Enter Pipe NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pipeNo">Pipe No <span className="text-red-500">*</span></Label>
                  <Input
                    id="pipeNo"
                    value={newMaterial.details?.pipeNo || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, pipeNo: value },
                      }));
                      if (pipeInventoryMode) {
                        setFitterPrefillOk(false);
                        setFitterPrefillMessage(null);
                      }
                    }}
                    onBlur={() => {
                      if (pipeInventoryMode) {
                        void syncPipeInventoryFitterPrefill();
                      }
                    }}
                    placeholder="Enter Pipe No"
                    disabled={fitterPrefillLoading && pipeInventoryMode}
                  />
                  {pipeInventoryMode && (
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {fitterPrefillLoading && (
                          <span className="inline-flex items-center gap-1 text-xs text-heritage-grey">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Syncing Pipe Fitter…
                          </span>
                        )}
                        {!fitterPrefillLoading && (fitterPrefillMessage || fitterPrefillOk) && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => void syncPipeInventoryFitterPrefill()}
                            disabled={fitterPrefillLoading}
                          >
                            Retry sync
                          </Button>
                        )}
                      </div>
                      {fitterPrefillMessage && (
                        <p
                          className={`text-xs ${fitterPrefillOk ? 'text-heritage-grey' : 'text-red-600'}`}
                        >
                          {fitterPrefillMessage}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pipeId">Pipe Id </Label>
                  <Input
                    id="pipeId"
                    value={newMaterial.details?.pipePipeId || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipePipeId: e.target.value }
                    }))}
                    placeholder="Enter Pipe Id"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="heatNumber">
                    Heat Number
                    {pipeInventoryMode && <span className="text-red-500"> *</span>}
                  </Label>
                  <Input
                    id="heatNumber"
                    value={newMaterial.details?.pipeHeatNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeHeatNumber: e.target.value }
                    }))}
                    placeholder="Enter Heat Number"
                  />
                </div>
                {newMaterial.materialCategory === 'Pipe' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="pipeLotNumber">Lot Number</Label>
                      <Input
                        id="pipeLotNumber"
                        value={newMaterial.details?.pipeLotNumber || ''}
                        onChange={(e) => setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeLotNumber: e.target.value }
                        }))}
                        placeholder="Enter Lot Number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pipeType">Pipe Type</Label>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={newMaterial.details?.pipeType}
                          onValueChange={(value: string) => {
                            setNewMaterial(prev => ({
                              ...prev,
                              details: { ...prev.details, pipeType: value }
                            }))
                          }}
                          placeholder="Select pipe type"
                          searchPlaceholder="Search pipe type..."
                          options={pipeTypeOptions.map((tag: { label: string }) => ({ id: tag.label, name: tag.label }))}
                          onAddNew={() => setIsAddingPipeType(true)}
                          addNewText="Add New Pipe Type"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pipePslLevel">PSL Level</Label>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={newMaterial.details?.pipePslLevel}
                          onValueChange={(value: string) => {
                            setNewMaterial(prev => ({
                              ...prev,
                              details: { ...prev.details, pipePslLevel: value }
                            }))
                          }}
                          placeholder="Select PSL level"
                          searchPlaceholder="Search PSL levels..."
                          options={pipePslLevelOptions.map((tag: { label: string }) => ({ id: tag.label, name: tag.label }))}
                          onAddNew={() => setIsAddingPipePslLevel(true)}
                          addNewText="Add New PSL Level"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pipeDelivery">Delivery</Label>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={newMaterial.details?.pipeDelivery}
                          onValueChange={(value: string) => {
                            setNewMaterial(prev => ({
                              ...prev,
                              details: { ...prev.details, pipeDelivery: value }
                            }))
                          }}
                          placeholder="Select delivery"
                          searchPlaceholder="Search delivery..."
                          options={pipeDeliveryOptions.map((tag: { label: string }) => ({ id: tag.label, name: tag.label }))}
                          onAddNew={() => setIsAddingPipeDelivery(true)}
                          addNewText="Add New Delivery"
                        />
                      </div>
                    </div>
                  </>
                )}
                {/* <div className="space-y-2">
                  <Label htmlFor="length">Length (ft)</Label>
                  <NumberInput
                    id="length"
                    value={newMaterial.details?.pipeLength}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeLength: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter Length"
                  />
                </div> */}
                {isDualDiameterMaterialCategory(newMaterial.materialCategory) ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="outerDiameter1">Diameter 1</Label>
                      <NumberInput
                        id="outerDiameter1"
                        showUnitSelect
                        unitValue={materialUnit.pipeOuterDiameter}
                        unitOptions={UNIT_OPTIONS}
                        onUnitChange={(v) => handleMaterialUnitChange('pipeOuterDiameter', v)}
                        value={newMaterial.details?.pipeOuterDiameter}
                        onChange={(value) => setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeOuterDiameter: value ? Number(value) : '' }
                        }))}
                        placeholder="Enter Diameter 1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wallThickness1">Wall Thickness 1</Label>
                      <NumberInput
                        id="wallThickness1"
                        showUnitSelect
                        unitValue={materialUnit.pipeWallThickness}
                        unitOptions={UNIT_OPTIONS}
                        onUnitChange={(v) => handleMaterialUnitChange('pipeWallThickness', v)}
                        value={newMaterial.details?.pipeWallThickness}
                        onChange={(value) => setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeWallThickness: value ? Number(value) : '' }
                        }))}
                        placeholder="Enter Wall Thickness 1"
                      />
                    </div>
                    
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="outerDiameter">Outer Diameter</Label>
                    <NumberInput
                      id="outerDiameter"
                      showUnitSelect
                      unitValue={materialUnit.pipeOuterDiameter}
                      unitOptions={UNIT_OPTIONS}
                      onUnitChange={(v) => handleMaterialUnitChange('pipeOuterDiameter', v)}
                      value={newMaterial.details?.pipeOuterDiameter}
                      onChange={(value) => setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, pipeOuterDiameter: value ? Number(value) : '' }
                      }))}
                      placeholder="Enter Outer Diameter"
                    />
                  </div>
                )}
                {isDualDiameterMaterialCategory(newMaterial.materialCategory) ? (
                  <>
                   <div className="space-y-2">
                      <Label htmlFor="outerDiameter2">Diameter 2</Label>
                      <NumberInput
                        id="outerDiameter2"
                        showUnitSelect
                        unitValue={materialUnit.pipeOuterDiameter}
                        unitOptions={UNIT_OPTIONS}
                        onUnitChange={(v) => handleMaterialUnitChange('pipeOuterDiameter', v)}
                        value={newMaterial.details?.pipeOuterDiameter2}
                        onChange={(value) => setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeOuterDiameter2: value ? Number(value) : '' }
                        }))}
                        placeholder="Enter Diameter 2"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wallThickness2">Wall Thickness 2</Label>
                      <NumberInput
                        id="wallThickness2"
                        showUnitSelect
                        unitValue={materialUnit.pipeWallThickness}
                        unitOptions={UNIT_OPTIONS}
                        onUnitChange={(v) => handleMaterialUnitChange('pipeWallThickness', v)}
                        value={newMaterial.details?.pipeWallThickness2}
                        onChange={(value) => setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeWallThickness2: value ? Number(value) : '' }
                        }))}
                        placeholder="Enter Wall Thickness 2"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="wallThickness">Wall Thickness</Label>
                    <NumberInput
                      id="wallThickness"
                      showUnitSelect
                      unitValue={materialUnit.pipeWallThickness}
                      unitOptions={UNIT_OPTIONS}
                      onUnitChange={(v) => handleMaterialUnitChange('pipeWallThickness', v)}
                      value={newMaterial.details?.pipeWallThickness}
                      onChange={(value) => setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, pipeWallThickness: value ? Number(value) : '' }
                      }))}
                      placeholder="Enter Wall Thickness"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label> Project Stage </Label>
                  <Select
                    value={newMaterial.details?.pipeProjectStage}
                    onValueChange={(value: 'PQT' | 'Production') =>
                      setNewMaterial(prev => ({ ...prev, details: { ...prev.details, pipeProjectStage: value } }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PQT">PQT</SelectItem>
                      <SelectItem value="Production">Production</SelectItem>

                    </SelectContent>
                  </Select>
                </div>
                {pipeInventoryMode && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-gray-700">Date Recorded</Label>
                      <AntdDate
                        name="dateRecorded"
                        value={
                          (newMaterial.details as { dateRecorded?: string } | undefined)?.dateRecorded ||
                          dayjs().format('YYYY-MM-DD')
                        }
                        onChange={(value: string) =>
                          setNewMaterial((prev) => ({
                            ...prev,
                            details: { ...prev.details, dateRecorded: value },
                          }))
                        }
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label>Pipe Grade</Label>
                  <MultiSelect
                    options={pipeGradeOptions}
                    selected={selectedPipeGrade}
                    onChange={(value: string[]) => {
                      setSelectedPipeGrade(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, pipeGradeIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select pipe grade"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Grade"
                    onAddButtonClick={() => setIsAddingPipeGrade(true)}
                    maxHeight="200px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Material Type</Label>
                  <MultiSelect
                    options={materialTypeOptions}
                    selected={selectedMaterialType}
                    onChange={(value: string[]) => {
                      setSelectedMaterialType(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, materialTypeIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select material type"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Material Type"
                    onAddButtonClick={() => setIsAddingMaterialType(true)}
                    maxHeight="200px"
                  />
                </div>


                <div className="space-y-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedManufacturer(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    // addButtonText="Add New Manufacturer"
                    // onAddButtonClick={() => setIsAddingManufacturer(true)}
                    maxHeight="200px"
                  />
                </div>

                <div className="space-y-2">

                  <Label htmlFor="customer"> Work Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.pipeWorkOrderNumber}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeWorkOrderNumber: value }
                        }))
                      }}
                      placeholder="Select work order number"
                      searchPlaceholder="Search work order numbers..."
                      options={workOrderNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingWorkOrderNumber(true)}
                      addNewText="Add New Work Order Number"
                    //    icon={<UserRound className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                    //  disabled={isLoadingData || loading}
                    //   loading={isLoadingData}
                    //   loadingText="Loading customers..."
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="poNumber">Purchase Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.pipePurchaseOrderNumber}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipePurchaseOrderNumber: value }
                        }))
                      }}
                      placeholder="Select Purchase Order number"
                      searchPlaceholder="Search Purchase Order numbers..."
                      options={poNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingPONumber(true)}
                      addNewText="Add New Purchase Order Number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.pipeLocation}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeLocation: value }
                        }))
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lengthReceived">Length</Label>
                  <NumberInput
                    id="lengthReceived"
                    showUnitSelect
                    unitValue={materialUnit.pipeLength}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('pipeLength', v)}
                    {...(pipeInventoryMode
                      ? {
                          min: 0,
                          step: 0.1,
                          wheelAdjust: true,
                          maxDecimalPlaces: 4,
                        }
                      : {})}
                    value={newMaterial.details?.pipeLength}
                    onChange={(value) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeLength:
                            value === '' || value == null
                              ? ''
                              : pipeInventoryMode
                                ? parseNonNegativeNumber(String(value))
                                : Number(value),
                        },
                      }))
                    }
                    placeholder="Enter length"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lengthRemaining">Length remaining</Label>
                  <NumberInput
                    id="lengthRemaining"
                    showUnitSelect
                    unitValue={materialUnit.pipeLengthRemaining}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('pipeLengthRemaining', v)}
                    value={newMaterial.details?.pipeLengthRemaining}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeLengthRemaining: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter length remaining"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weightPerFt">Weight per ft (lbs)</Label>
                  <NumberInput
                    id="weightPerFt"
                    value={newMaterial.details?.pipeWeightPerFt}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeWeightPerFt: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter weight per foot in pounds"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wpsNumber">WPS Number</Label>
                  <Input
                    id="wpsNumber"
                    value={newMaterial.details?.pipeExtraDetails?.wpsNumber ?? ''}
                    onChange={(e) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeExtraDetails: {
                            ...(prev.details?.pipeExtraDetails || {}),
                            wpsNumber: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="Enter WPS number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contractor">Contractor</Label>
                  <Input
                    id="contractor"
                    value={newMaterial.details?.pipeExtraDetails?.contractor ?? ''}
                    onChange={(e) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeExtraDetails: {
                            ...(prev.details?.pipeExtraDetails || {}),
                            contractor: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="Enter contractor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="numberOfPups">Number of pups</Label>
                  <NumberInput
                    id="numberOfPups"
                    value={newMaterial.details?.pipeExtraDetails?.numberOfPups ?? ''}
                    min={0}
                    step={1}
                    wheelAdjust
                    onChange={(value) => {
                      if (pipeInventoryMode) {
                        handleInventoryPupsChange(value);
                        return;
                      }
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeExtraDetails: {
                            ...(prev.details?.pipeExtraDetails || {}),
                            numberOfPups:
                              value === '' || value == null
                                ? ''
                                : parseNonNegativeInteger(value),
                          },
                        },
                      }));
                    }}
                    placeholder="Enter number of pups"
                  />
                </div>

                {pipeInventoryMode && (
                  <div className="space-y-2">
                    <Label className="invisible select-none text-sm font-medium" aria-hidden="true">
                      MTR received
                    </Label>
                    <div className="flex h-10 items-center gap-2">
                      <Checkbox
                        id="inventoryMtrReceived"
                        checked={Boolean(
                          (newMaterial.details as { mtrReceived?: boolean } | undefined)?.mtrReceived
                        )}
                        onCheckedChange={(c) =>
                          setNewMaterial((prev) => ({
                            ...prev,
                            details: { ...prev.details, mtrReceived: c === true },
                          }))
                        }
                      />
                      <Label htmlFor="inventoryMtrReceived" className="font-normal cursor-pointer">
                        MTR received
                      </Label>
                    </div>
                  </div>
                )}

                {pipeInventoryMode && inventorySegments.length > 0 && (
                  <div className="space-y-3 col-span-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-medium">Pipe Segments</h3>
                      <Button
                        type="button"
                        onClick={() => {
                          const next = [
                            ...inventorySegments,
                            createEmptyWarehouseSegment(
                              inventorySegments.length,
                              (newMaterial.details as { dateRecorded?: string } | undefined)
                                ?.dateRecorded || dayjs().format('YYYY-MM-DD')
                            ),
                          ];
                          setInventorySegments(next, next.length);
                        }}
                        className="flex items-center gap-2"
                        size="sm"
                      >
                        <Plus className="h-4 w-4" />
                        Add Segment
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Segment Name
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Segment Length
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Date Inventory
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Used
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {inventorySegments.map((segment) => (
                            <tr key={segment.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <Input
                                  value={segment.segmentName}
                                  onChange={(e) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? { ...s, segmentName: e.target.value }
                                          : s
                                      )
                                    )
                                  }
                                  className="w-full min-w-[120px]"
                                  placeholder="A"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <NumberInput
                                  value={segment.segmentLength}
                                  min={0}
                                  step={0.1}
                                  wheelAdjust
                                  emptyWhenZero={false}
                                  maxDecimalPlaces={4}
                                  onChange={(value) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? {
                                              ...s,
                                              segmentLength:
                                                value === ''
                                                  ? 0
                                                  : parseNonNegativeNumber(value),
                                            }
                                          : s
                                      )
                                    )
                                  }
                                  className="w-full min-w-[140px]"
                                  placeholder="0.0"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <AntdDate
                                  type="date"
                                  value={segment.dateInventory || ''}
                                  onChange={(dateVal) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? {
                                              ...s,
                                              dateInventory: dateVal
                                                ? String(dateVal).slice(0, 10)
                                                : '',
                                            }
                                          : s
                                      )
                                    )
                                  }
                                  className="w-full min-w-[160px]"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={segment.used}
                                  onCheckedChange={(checked) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? { ...s, used: checked === true }
                                          : s
                                      )
                                    )
                                  }
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Button
                                  type="button"
                                  onClick={() => {
                                    const next = inventorySegments.filter((s) => s.id !== segment.id);
                                    setInventorySegments(next, next.length ? next.length : '');
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Marking</Label>
                  <textarea
                    value={newMaterial.details?.pipeMarking || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeMarking: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 min-h-[60px] rounded-md  resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter marking..."
                  />
                </div>
                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Notes</Label>
                  <textarea
                    value={newMaterial.details?.pipeNotes || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeNotes: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter notes..."
                  />
                </div>

              </>
            )}

            {newMaterial.materialCategory === 'Forge' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="forgeNRI">Forge NRI <span className="text-red-500">*</span></Label>
                  <Input
                    id="forgeNRI"
                    value={newMaterial.details?.forgeNRI || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeNRI: e.target.value }
                    }))}
                    placeholder="Enter Forge NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forgeNRI">Forge Serial Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="forgeNRI"
                    value={newMaterial.details?.forgeSerialNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,

                      details: { ...prev.details, forgeSerialNumber: e.target.value, forgeOrderNumber: e.target.value }
                    }))}
                    placeholder="Enter Forge NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forgeHeatNumber">Forge Heat Number</Label>
                  <Input
                    id="forgeHeatNumber"
                    value={newMaterial.details?.forgeHeatNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeHeatNumber: e.target.value }
                    }))}
                    placeholder="Enter Forge Heat Number"
                  />
                </div>


                <div className="space-y-2">

                  <Label htmlFor="customer"> Work Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.forgeWorkOrderNumber}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: {
                            ...prev.details,
                            forgeOrderNumber: value,
                            forgeWorkOrderNumber: value,
                          }
                        }))
                      }}
                      placeholder="Select work order number"
                      searchPlaceholder="Search work order numbers..."
                      options={workOrderNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingWorkOrderNumber(true)}
                      addNewText="Add New Work Order Number"
                    //    icon={<UserRound className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />}
                    //  disabled={isLoadingData || loading}
                    //   loading={isLoadingData}
                    //   loadingText="Loading customers..."
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="poNumber">Purchase Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.forgePurchaseOrderNumber}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, forgePurchaseOrderNumber: value }
                        }))
                      }}
                      placeholder="Select Purchase Order number"
                      searchPlaceholder="Search Purchase Order numbers..."
                      options={poNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingPONumber(true)}
                      addNewText="Add New Purchase Order Number"
                    />
                  </div>
                </div>





                <div className="space-y-2">
                  <Label htmlFor="outerDiameter">Outer Diameter ({unit})</Label>
                  <NumberInput
                    id="outerDiameter"
                    value={newMaterial.details?.forgeOuterDiameter}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeOuterDiameter: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter Outer Diameter"
                  />
                </div>
                <div className="space-y-2"  >
                  <Label htmlFor="wallThickness">Wall Thickness ({unit})</Label>
                  <NumberInput
                    id="wallThickness"
                    value={newMaterial.details?.forgeWallThickness}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeWallThickness: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter Wall Thickness"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Forge Grade</Label>
                  <MultiSelect
                    options={pipeGradeOptions}
                    selected={selectedPipeGrade}
                    onChange={(value: string[]) => {
                      setSelectedPipeGrade(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, forgeGradeIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select forge grade"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Grade"
                    onAddButtonClick={() => setIsAddingPipeGrade(true)}
                    maxHeight="200px"
                  />
                </div>



                <div className="space-y-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedManufacturer(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    // addButtonText="Add New Manufacturer"
                    // onAddButtonClick={() => setIsAddingManufacturer(true)}
                    maxHeight="200px"
                  />
                </div>


                <div className="space-y-2">
                  <Label> Project Stage </Label>
                  <Select
                    value={newMaterial.details?.forgeProjectStage}
                    onValueChange={(value: 'PQT' | 'Production') =>
                      setNewMaterial(prev => ({ ...prev, details: { ...prev.details, forgeProjectStage: value } }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PQT">PQT</SelectItem>
                      <SelectItem value="Production">Production</SelectItem>

                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.forgeLocation}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, forgeLocation: value }
                        }))
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>
                <div className="space-y-2"  >
                  <Label htmlFor="wallThickness">Length (ft)</Label>
                  <NumberInput
                    id="wallThickness"
                    value={(newMaterial?.details?.forgeLength) || ''}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeLength: value ? (Number(value)) : '' }
                    }))}
                    placeholder="Enter Length"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lengthRemaining">Length remaining(ft)</Label>
                  <NumberInput
                    id="lengthRemaining"
                    value={newMaterial.details?.forgeLengthRemaining}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeLengthRemaining: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter length remaining in feet"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weightPerFt">Weight per ft (lbs)</Label>
                  <NumberInput
                    id="weightPerFt"
                    value={newMaterial.details?.forgeWeightPerFt}
                    onChange={(value) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeWeightPerFt: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter weight per foot in pounds"
                  />
                </div>

                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Notes</Label>
                  <textarea
                    value={newMaterial.details?.forgeNotes || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeNotes: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter forge notes..."
                  />
                </div>
              </>
            )}

            {newMaterial.materialCategory === 'Wire Lot' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="wireLotNumber">Lot/Certification Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="wireLotNumber"
                    value={newMaterial.details?.wireLotNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireLotNumber: e.target.value }
                    }))}
                    placeholder="Enter Lot/Certification Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireType">Consumable Type</Label>
                  <Input
                    id="wireType"
                    value={newMaterial.details?.wireType || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireType: e.target.value }
                    }))}
                    placeholder="Enter Consumable Type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireSizeNumber">Size</Label>
                  <Input
                    id="wireSizeNumber"
                    value={newMaterial.details?.wireSizeNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireSizeNumber: e.target.value }
                    }))}
                    placeholder="Enter Wire Size"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireSerialNumber">Serial Number </Label>
                  <Input
                    id="wireSerialNumber"
                    value={newMaterial.details?.wireSerialNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireSerialNumber: e.target.value }
                    }))}
                    placeholder="Enter Wire Serial Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireComposition">Wire Classification</Label>
                  <Input
                    id="wireClassification"
                    value={newMaterial.details?.wireExtraDetails?.wireClassification || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireExtraDetails: { ...prev.details.wireExtraDetails, wireClassification: e.target.value } }
                    }))}
                    placeholder="Enter Wire Classification"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Manufacturer</Label>
                  <Input
                    id="wireManufacturer"
                    value={newMaterial.details?.wireExtraDetails?.wireManufacturer || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireExtraDetails: { ...prev.details.wireExtraDetails, wireManufacturer: e.target.value } }
                    }))}
                    placeholder="Enter Manufacturer"
                  />
                  {/* <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedWireManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedWireManufacturer(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    // addButtonText="Add New Manufacturer"
                    // onAddButtonClick={() => setIsAddingManufacturer(true)}
                    maxHeight="200px"
                  /> */}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireBatchNumber">Batch Number</Label>
                  <Input
                    id="wireBatchNumber"
                    value={newMaterial.details?.wireBatchNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireBatchNumber: e.target.value }
                    }))}
                    placeholder="Enter Batch Number"
                  />
                </div>


              </>
            )}

            {newMaterial.materialCategory === 'Gas' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="gasSerialNumber">Clyinder/Serial Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="gasSerialNumber"
                    value={newMaterial.details?.gasSerialNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasSerialNumber: e.target.value }
                    }))}
                    placeholder="Enter Gas Serial Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasLotNumber">Lot Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="gasLotNumber"
                    value={newMaterial.details?.gasLotNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasLotNumber: e.target.value }
                    }))}
                    placeholder="Enter Gas Lot Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasType">Consumable Type</Label>
                  <Input
                    id="gasType"
                    value={newMaterial.details?.gasType || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasType: e.target.value }
                    }))}
                    placeholder="Enter Consumable Type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasSize">Size</Label>
                  <Input
                    id="gasSize"
                    value={newMaterial.details?.gasExtraDetails?.gasSize || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasExtraDetails: { ...prev.details.gasExtraDetails, gasSize: e.target.value } }
                    }))}
                    placeholder="Enter Gas Size"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasBatchNumber">Gas Composition</Label>
                  <Input
                    id="gasComposition"
                    value={newMaterial.details.gasExtraDetails?.gasComposition || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasExtraDetails: { ...prev.details.gasExtraDetails, gasComposition: e.target.value } }
                    }))}
                    placeholder="Enter Gas Composition"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label>Manufacturer</Label>
                  <Input
                    id="gasManufacturer"
                    value={newMaterial.details?.gasExtraDetails?.gasManufacturer || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasExtraDetails: { ...prev.details.gasExtraDetails, gasManufacturer: e.target.value } }
                    }))}
                    placeholder="Enter Manufacturer"
                  />
                  {/* <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedGasManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedGasManufacturer(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                  //  showAddButton={true}
                  //addButtonText="Add New Manufacturer"
                  //onAddButtonClick={() => setIsAddingManufacturer(true)}
                  //maxHeight="200px"
                  /> */}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasBatchNumber">Batch Number</Label>
                  <Input
                    id="gasBatchNumber"
                    value={newMaterial.details?.gasBatchNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasBatchNumber: e.target.value }
                    }))}
                    placeholder="Enter Batch Number"
                  />
                </div>


              </>
            )}

            {newMaterial.materialCategory === 'Coating' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="coatingLotNumber">Lot Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="coatingLotNumber"
                    value={newMaterial.details?.coatingLotNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, coatingLotNumber: e.target.value }
                    }))}
                    placeholder="Enter Lot Number"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label>Manufacturer</Label>
                  <Input
                    id="coatingManufacturer"
                    value={newMaterial.details?.coatingExtraDetails?.coatingManufacturer || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, coatingExtraDetails: { ...prev.details.coatingExtraDetails, coatingManufacturer: e.target.value } }
                    }))}
                    placeholder="Enter Manufacturer"
                  />
                  {/* <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedCoatingManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedCoatingManufacturer(value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    // addButtonText="Add New Manufacturer"
                    // onAddButtonClick={() => setIsAddingManufacturer(true)}
                    maxHeight="200px"
                  /> */}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coatingBatchNumber">Batch Number</Label>
                  <Input
                    id="coatingBatchNumber"
                    value={newMaterial.details?.coatingBatchNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, coatingBatchNumber: e.target.value }
                    }))}
                    placeholder="Enter Batch Number"
                  />
                </div>
              </>
            )}

            {newMaterial.materialCategory === 'Misc' && (
              <>
                <div className="space-y-2">
                  <Label>
                    Material type <span className="text-red-500">*</span>
                  </Label>
                  <SearchableSelect
                    value={newMaterial.details?.miscMaterialTypeName}
                    onValueChange={(value: string) => {
                      const opt =
                        miscMaterialTypeSelectOptions.find((o) => o.label === value) ??
                        miscMaterialTypeOptions.find((o) => o.label === value) ??
                        materialTypeOptions.find((o) => o.label === value);
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          miscMaterialTypeName: value,
                          miscMaterialTypeIds: opt ? [Number(opt.value)] : [],
                        },
                      }));
                    }}
                    placeholder="Select misc material type"
                    searchPlaceholder="Search misc material types..."
                    options={miscMaterialTypeSelectOptions.map((tag) => ({
                      id: tag.value,
                      name: tag.label,
                    }))}
                    onAddNew={() => setIsAddingMiscMaterialType(true)}
                    addNewText="Add New Misc Material Type"
                    showAddButton={true}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscPartNumber">Part number</Label>
                  <Input
                    id="miscPartNumber"
                    value={newMaterial.details?.miscPartNumber || ''}
                    onChange={(e) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscPartNumber: e.target.value },
                      }))
                    }
                    placeholder="Enter part number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscHeatNumber">Heat number</Label>
                  <Input
                    id="miscHeatNumber"
                    value={newMaterial.details?.miscHeatNumber || ''}
                    onChange={(e) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscHeatNumber: e.target.value },
                      }))
                    }
                    placeholder="Enter heat number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Lot number</Label>
                  <SearchableSelect
                    value={newMaterial.details?.miscLotNumber}
                    onValueChange={(value: string) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscLotNumber: value },
                      }))
                    }
                    placeholder="Select lot number"
                    searchPlaceholder="Search lot numbers..."
                    options={miscLotNumberTagOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                    onAddNew={() => setIsAddingMiscLot(true)}
                    addNewText="Add New Lot Number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Job number</Label>
                  <SearchableSelect
                    value={newMaterial.details?.miscJobNumber}
                    onValueChange={(value: string) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscJobNumber: value },
                      }))
                    }
                    placeholder="Select job number"
                    searchPlaceholder="Search job numbers..."
                    options={workOrderNumberOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                    onAddNew={() => setIsAddingWorkOrderNumber(true)}
                    addNewText="Add New Job Number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>PO number</Label>
                  <SearchableSelect
                    value={newMaterial.details?.miscPoNumber}
                    onValueChange={(value: string) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscPoNumber: value },
                      }))
                    }
                    placeholder="Select PO number"
                    searchPlaceholder="Search PO numbers..."
                    options={poNumberOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                    onAddNew={() => setIsAddingPONumber(true)}
                    addNewText="Add New PO Number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscQuantity">
                    Quantity <span className="text-red-500">*</span>
                  </Label>
                  <NumberInput
                    id="miscQuantity"
                    value={newMaterial.details?.miscQuantity}
                    onChange={(value) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscQuantity: value !== '' && value != null ? Number(value) : '' },
                      }))
                    }
                    placeholder="Enter quantity"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscSizeOrWeight">Size / weight</Label>
                  <Input
                    id="miscSizeOrWeight"
                    value={newMaterial.details?.miscSizeOrWeight || ''}
                    onChange={(e) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscSizeOrWeight: e.target.value },
                      }))
                    }
                    placeholder="e.g. 10 ft, 25 kg"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedManufacturer(value);
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map((id) => Number(id)) },
                      }));
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    maxHeight="200px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Production type</Label>
                  <Select
                    value={
                      newMaterial.details?.miscProductionType ??
                      newMaterial.details?.miscProjectStage
                    }
                    onValueChange={(value: 'Welding' | 'Coating') =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          miscProductionType: value,
                          miscProjectStage: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select production type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Welding">Welding</SelectItem>
                      <SelectItem value="Coating">Coating</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={newMaterial.details?.miscLocation}
                      onValueChange={(value: string) => {
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, miscLocation: value }
                        }))
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>

                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Note</Label>
                  <textarea
                    value={newMaterial.details?.miscNotes || ''}
                    onChange={(e) =>
                      setNewMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscNotes: e.target.value },
                      }))
                    }
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-400"
                    placeholder="Notes..."
                  />
                </div>
              </>
            )}

            {newMaterial.materialCategory === 'NDE' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ndeConsumableType">Consumable Type <span className="text-red-500">*</span></Label>
                  <Input
                    id="ndeConsumableType"
                    value={newMaterial.details?.consumableType || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, consumableType: e.target.value }
                    }))}
                    placeholder="Enter consumable type"
                    className="w-full"
                    required
                  />
                </div>


                {/* Serial Number */}
                <div className="space-y-2">
                  <Label htmlFor="ndeSerialNumber">Serial Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="ndeSerialNumber"
                    value={newMaterial.details?.serialNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, serialNumber: e.target.value }
                    }))}
                    placeholder="Enter serial number"
                    className="w-full"
                  />
                </div>

                {/* Type */}
                <div className="space-y-2">
                  <Label htmlFor="ndeType">Type</Label>
                  <Input
                    id="ndeType"
                    value={newMaterial.details?.type || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, type: e.target.value }
                    }))}
                    placeholder="Enter type (e.g., ER70S-6)"
                    className="w-full"
                  />
                </div>
                {/* Lot Number */}
                <div className="space-y-2">
                  <Label htmlFor="lotNumber">Lot Number</Label>
                  <Input
                    id="lotNumber"
                    value={newMaterial.details?.lotNumber || ''}
                    onChange={(e) => setNewMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, lotNumber: e.target.value }
                    }))}
                    placeholder="Enter lot number"
                    className="w-full"
                  />
                </div>



                {/* Expiry Date */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Expiry Date</Label>
                  <div className="flex items-center space-x-2">
                    <AntdDate
                      name="ndeExpDate"
                      value={ndeExpDate}
                      onChange={(value: string) => {
                        setNdeExpDate(value);
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, expDate: value }
                        }));
                      }}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Material ID */}
                <div className="space-y-2">
                  <Label htmlFor="ndeMaterialId">Material ID</Label>
                  <Input
                    id="ndeMaterialId"
                    value={ndeMaterialId}
                    onChange={(e) => {
                      setNdeMaterialId(e.target.value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { 
                          ...prev.details, 
                          ndeExtraDetails: { 
                            ...prev.details.ndeExtraDetails, 
                            ndeMaterialId: e.target.value 
                          }
                        }
                      }));
                    }}
                    placeholder="Enter material ID"
                    className="w-full"
                  />
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="ndeLocation">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={ndeSelectedLocation}
                      onValueChange={(value: string) => {
                        setNdeSelectedLocation(value);
                        setNewMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, location: value }
                        }));
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>
                {/* Manufacturer */}
                <div className="space-y-2 col-span-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedNDEManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedNDEManufacturer(value);
                      // Get labels from manufacturerOptions based on selected IDs
                      const selectedLabels = value.map((id: string) => {
                        const option = manufacturerOptions.find((opt: any) => opt.value === id);
                        return option ? option.label : '';
                      }).filter((label: string) => label !== '');
                      
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { 
                          ...prev.details, 
                          manufacturerIds: value.map(id => Number(id)),
                          ndeExtraDetails: { 
                            ...prev.details.ndeExtraDetails, 
                            ndeManufacturer: selectedLabels.join(', ') 
                          }
                        }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Manufacturer"
                    onAddButtonClick={() => setIsAddingManufacturer(true)}
                    maxHeight="200px"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Notes</Label>
                  <textarea
                    value={ndeNotes || ''}
                    onChange={(e) => {
                      setNdeNotes(e.target.value);
                      setNewMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, notes: e.target.value }
                      }));
                    }}
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter notes..."
                  />
                </div>
              </>
            )}

            {/* Certification Selection Section */}
            {/* <div className="col-span-2 space-y-4">
              <div className="space-y-2">
                <Label>Related Certifications</Label>
                <p className="text-sm text-gray-600">
                  Select certifications that are related to this material (optional).
                </p>
                
                {certificationsLoading ? (
                  <div className="text-sm text-gray-500">Loading certifications...</div>
                ) : certificationsError ? (
                  <div className="text-sm text-red-500">Error loading certifications: {certificationsError}</div>
                ) : (
                  <MultiSelect
                    options={certificationOptions}
                    selected={selectedCertifications}
                    onChange={setSelectedCertifications}
                    placeholder="Select related certifications"
                    isSearchable={true}
                  />
                )}
              </div>
            </div> */}

            {/* Document Upload Section - Available for Pipe and Forge */}
            {(
              <div className="col-span-4 space-y-2 ">
                <div className="space-y-2">
                  <Label>Upload Documents</Label>
                  <p className="text-sm text-gray-600">
                    Upload the material documents.
                    <br />
                    <span className="text-xs text-gray-500">
                      Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                      <br />
                      Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL} per file
                    </span>
                  </p>

                  <FileUpload
                    onFileSelect={handleMaterialFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                    multiple={true}
                    maxFiles={100}
                    buttonText="Upload Documents"
                    variant="outline"
                    size="lg"
                    onRemoveFile={handleRemoveFile}
                    selectedFiles={materialDocuments}
                    showFileList={materialDocuments.length > 0}
                    className="w-full"
                    showTags={true}
                    optionsTags={tagOptions}
                    selectedTags={selectedTags}
                    showAddButton={true}
                    addButtonText="Add New Tag"
                    onAddButtonClick={() => setIsAddingTag(true)}
                    onTagsChange={handleTagsChange}
                    maxHeight=''
                  />

                  {/* Debug: Show current documentFiles count */}
                  <div className="text-xs text-gray-500">
                    Current files: {materialDocuments.length} file(s)
                  </div>
                </div>

                {/* {materialDocuments.length > 0 && (
                  <div className="space-y-2">
                    <Label>Document Tags</Label>
                    <MultiSelect
                      options={tagOptions}
                      selected={selectedTags}
                      onChange={setSelectedTags}
                      placeholder="Select or search tags"
                      isSearchable={true}
                      className="w-full"
                      showAddButton={true}
                      addButtonText="Add New Tag"
                      onAddButtonClick={() => setIsAddingTag(true)}
                      maxHeight="200px"
                    />
                  </div>
                )} */}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-3 mt-6 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddMaterial}
            className="bg-[#EB1C37] text-white px-4 py-2 rounded-lg hover:bg-[#EB1C37]/90 transition-colors"
            disabled={
              !newMaterial.materialCategory ||
              isSubmitting ||
              (pipeInventoryMode && fitterPrefillLoading)
            }
            loading={isSubmitting}
            loadingText="Adding Material..."
          >
            {'Add Material'}
          </Button>

           {/* <Button
            onClick={updateNewPipesToDatabase}
            className="bg-[#EB1C37] text-white px-4 py-2 rounded-lg hover:bg-[#EB1C37]/90 transition-colors"
            disabled={isUpdatingPipes || isSubmitting}
            loading={isUpdatingPipes}
            loadingText={newMaterial.materialCategory === 'Forge' ? 'Updating Forges...' : 'Updating Pipes...'}
          >
            {newMaterial.materialCategory === 'Forge' ? 'Update Forges' : 'Update Pipes'}
          </Button>  */}
        </DialogFooter>
      </DialogContent>

      {/* Add New Tag Dialog */}
      {isAddingTag && <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />}

      {/* Add New Pipe Grade Dialog */}
      {isAddingPipeGrade && <AddNewItemDialog
        open={isAddingPipeGrade}
        onOpenChange={setIsAddingPipeGrade}
        title="Add New Grade"
        label="Pipe Grade"
        placeholder="Enter pipe grade"
        onAdd={handleAddPipeGrade}
        buttonText="Add Grade"
        cancelText="Cancel"
      />}
      {/* Add New Material Type Dialog */}
      {isAddingMaterialType && <AddNewItemDialog
        open={isAddingMaterialType}
        onOpenChange={setIsAddingMaterialType}
        title="Add New Material Type"
        label="Material Type"
        placeholder="Enter material type"
        onAdd={handleAddMaterialType}
        buttonText="Add Material Type"
        cancelText="Cancel"
      />}
      {/* Add New manufacturer Dialog */}
      {isAddingManufacturer && <AddNewItemDialog
        open={isAddingManufacturer}
        onOpenChange={setIsAddingManufacturer}
        title="Add New Manufacturer"
        label="Manufacturer"
        placeholder="Enter manufacturer"
        onAdd={handleAddManufacturer}
        buttonText="Add Manufacturer"
        cancelText="Cancel"
      />}
      {isAddingWorkOrderNumber && <AddNewItemDialog
        open={isAddingWorkOrderNumber}
        onOpenChange={setIsAddingWorkOrderNumber}
        title="Add New Work Order Number"
        label="Work Order Number"
        placeholder="Enter work order number"
        onAdd={handleAddWorkOrderNumber}
        buttonText="Add Work Order Number"
        cancelText="Cancel"
      />}
      {isAddingPONumber && <AddNewItemDialog
        open={isAddingPONumber}
        onOpenChange={setIsAddingPONumber}
        title="Add New PO Number"
        label="PO Number"
        placeholder="Enter PO number"
        onAdd={handleAddPONumber}
        buttonText="Add PO Number"
        cancelText="Cancel"
      />}
      {isAddingLocation && <AddNewItemDialog
        open={isAddingLocation}
        onOpenChange={setIsAddingLocation}
        title="Add New Location"
        label="Location"
        placeholder="Enter location"
        onAdd={handleAddLocation}
        buttonText="Add Location"
        cancelText="Cancel"
      />}
      {isAddingPipeType && <AddNewItemDialog
        open={isAddingPipeType}
        onOpenChange={setIsAddingPipeType}
        title="Add New Pipe Type"
        label="Pipe Type"
        placeholder="Enter pipe type"
        onAdd={handleAddPipeType}
        buttonText="Add Pipe Type"
        cancelText="Cancel"
      />}
      {isAddingPipePslLevel && <AddNewItemDialog
        open={isAddingPipePslLevel}
        onOpenChange={setIsAddingPipePslLevel}
        title="Add New PSL Level"
        label="PSL Level"
        placeholder="Enter PSL level"
        onAdd={handleAddPipePslLevel}
        buttonText="Add PSL Level"
        cancelText="Cancel"
      />}
      {isAddingPipeDelivery && <AddNewItemDialog
        open={isAddingPipeDelivery}
        onOpenChange={setIsAddingPipeDelivery}
        title="Add New Delivery"
        label="Delivery"
        placeholder="Enter delivery"
        onAdd={handleAddPipeDelivery}
        buttonText="Add Delivery"
        cancelText="Cancel"
      />}
      {isAddingMiscLot && (
        <AddNewItemDialog
          open={isAddingMiscLot}
          onOpenChange={setIsAddingMiscLot}
          title="Add Lot Number"
          label="Lot number"
          placeholder="Enter lot number"
          onAdd={handleAddMiscLotNumber}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}
      {isAddingMiscMaterialType && (
        <AddNewItemDialog
          open={isAddingMiscMaterialType}
          onOpenChange={setIsAddingMiscMaterialType}
          title="Add Misc Material Type"
          label="Misc material type"
          placeholder="Enter misc material type"
          onAdd={handleAddMiscMaterialType}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}
    </Dialog>
  );
}; 